/* English dictionary — primary source of truth.
 * Anything missing in another locale falls back to this file. */
const en = {
  common: {
    upgrade_pro: "Upgrade to Pro",
    upgrade_premium: "Upgrade to Premium",
    save_pct: "Save {pct}%",
    save_eur_yr: "Save \u20AC{amount}/yr",
    cancel: "Cancel",
    confirm: "Confirm",
    save: "Save",
    saving: "Saving\u2026",
    loading: "Loading\u2026",
    learn_more: "Learn more",
    add: "Add",
    none: "None",
    done: "Done",
    just_now: "just now",
    minutes_ago: "{n} min ago",
    hours_ago: "{n}h ago",
    days_ago: "{n}d ago",
    months_ago: "{n}mo ago",
    back_to_landing: "Back to landing",
    refresh: "Refresh",
    or: "or",
    per_month_short: "/mo",
    billed_yearly: "Billed \u20AC{amount}/year",
    billed_yearly_short: "Billed \u20AC{amount}/yr \u00B7 save {pct}%",
    or_yearly_short: "Or \u20AC{amount}/yr (save {pct}%)",
    current: "Current",
    current_plan: "Current plan",
    coming_soon: "Coming soon",
    plan: "Plan",
    free_label: "Free",
    pro_label: "Pro",
    premium_label: "Premium",
  },
  language: {
    label: "Language",
    english: "English",
    german: "Deutsch",
  },
  nav: {
    analyze: "Analyze",
    watchlist: "Watchlist",
    portfolio: "Portfolio",
    pricing: "Pricing",
    billing_usage: "Billing & usage",
    log_in: "Log in",
    log_out: "Log out",
    get_started: "Get started",
    signed_in_as: "Signed in as",
    search_placeholder: "Search any stock ticker",
    ai_badge: "AI",
  },
  footer: {
    tagline: "AI-powered stock analysis. Find undervalued stocks in seconds.",
    product: "Product",
    legal: "Legal",
    disclaimer: "Disclaimer",
    disclaimer_body: "This platform provides educational stock analysis only and is not financial advice. Analyses may use market and fundamental data from Financial Modeling Prep. Data may be delayed, stale, estimated or incomplete depending on availability.",
    rights: "\u00A9 {year} Stockaily \u00B7 stockaily.ai",
    built_for: "Built for serious retail investors.",
    link_analyze: "Analyze",
    link_pricing: "Pricing",
    link_watchlist: "Watchlist",
    link_portfolio: "Portfolio",
    link_login: "Log in",
    link_privacy: "Privacy Policy",
    link_terms: "Terms",
    link_cookies: "Cookie Policy",
    link_impressum: "Impressum",
  },
  // Phase 8.LP.LR v1.8 — Dedicated /analyze landing page copy.
  analyze_landing: {
    hero_kicker: "STOCK ANALYSIS",
    hero_title: "Analyze any stock in seconds",
    hero_body: "See Fair Value, AI Opportunity Score, risk level and AI insights in a clean, institutional-style analysis.",
    search_placeholder: "Search any stock ticker or company",
    preview_label: "Example analysis preview",
    preview_caption: "This is what a real Stockaily analysis looks like.",
    signal_undervalued: "Undervalued",
    signal_fairly_valued: "Fairly Valued",
    signal_overvalued: "Overvalued",
    risk_low: "Low Risk",
    risk_medium: "Medium Risk",
    risk_high: "High Risk",
    horizon_short: "Short-term",
    horizon_mid: "Mid-term",
    horizon_long: "Long-term",
    live_data_chip: "Live data",
    section_score: "AI Opportunity Score",
    section_score_hint: "Composite valuation signal, 0-100.",
    section_snapshot: "Institutional Snapshot",
    section_snapshot_hint: "The one-line take on the stock.",
    section_ai_insights: "AI Insights",
    section_key_drivers: "Key Drivers",
    section_chart: "Price History",
    section_kpis: "Key Financial Metrics",
    ai_summary_title: "Summary",
    kd_undervalued_label: "Trading below fair value",
    kd_positive_growth_label: "Positive revenue growth",
    kd_healthy_margins_label: "Healthy operating margins",
    kd_reasonable_multiples_label: "Reasonable valuation multiples",
    lock_bull_title: "Bull Case",
    lock_bear_title: "Bear Case",
    lock_risks_title: "Key Risks",
    lock_confidence_title: "Confidence Reasoning",
    lock_alpha_title: "AI Alpha Signals",
    lock_hint: "Sign up to unlock the full analysis.",
    cta_signed_out_title: "Ready to analyze your own stock picks?",
    cta_signed_out_body: "Create a free account and run your first analysis in seconds.",
    cta_signed_out_primary: "Create free account",
    cta_signed_out_secondary: "See pricing",
    cta_signed_in_title: "Run a fresh analysis",
    cta_signed_in_body: "Type a ticker above or pick from your watchlist.",
    cta_signed_in_secondary: "Go to Watchlist",
    kpi_market_cap: "Market Cap",
    kpi_pe: "P/E",
    kpi_ps: "P/S",
    kpi_pb: "P/B",
    kpi_revenue_growth: "Rev Growth",
    kpi_operating_margin: "Op. Margin",
    kpi_current_price: "Current Price",
    kpi_fair_value: "Fair Value",
    kpi_upside: "Upside",
    snapshot_body: "Trading modestly below our fair value estimate with healthy operating margins and steady revenue growth. Balance sheet quality supports the current risk profile.",
    ai_summary_body: "Stockaily's valuation engine currently reads the position as undervalued versus its intrinsic estimate. The composite score reflects solid fundamentals, reasonable multiples relative to peers, and a manageable risk profile.",
    ai_summary_body_pypl: "PayPal Holdings, Inc. operates within the Financials sector, particularly in Credit Services. The stock is classified as a 'Value' stock with a current price of 44.28, translating to a market capitalization of approximately 39.06 billion. The company is currently considered undervalued, with a fair value estimate ranging between 43.46 and 80.91, suggesting a potential upside of 40.43%. However, fair value confidence is low, indicating uncertainty in the valuation.",
    locked_strip_title: "See the full institutional analysis",
    locked_strip_body: "Bull case, bear case, key risks, confidence reasoning and the AI Alpha detail unlock with a free Stockaily account.",
    inline_cta_title: "Start free to unlock more of this analysis",
    inline_cta_subtext: "Run your first stock analysis in seconds.",
    inline_cta_body: "Start free to unlock more of this analysis.",
    inline_cta_button: "Create free account",
  },
  // Phase 8.LP.LR v1.3 — signed-in Analyze Hub.
  analyze_hub: {
    hero_body:
      "Search a ticker to see Fair Value, AI Opportunity Score, risk level and AI insights.",
    usage_loading: "Loading your plan…",
    usage_unlimited: "Unlimited analyses active",
    usage_analyses_remaining: "analyses remaining",
    usage_first_period: "first 30 days",
    usage_rolling_window: "rolling 30 days",
    usage_exhausted: "You've used all your free analyses.",
    usage_exhausted_link: "See plans",
    usage_unavailable: "Usage state unavailable — you can still search below.",
    recent_title: "Recent analyses",
    recent_empty_body:
      "No recent analyses yet. Search a ticker above to start your first analysis.",
    recent_open: "Open analysis",
    education_title: "How to read the AI Opportunity Score",
    education_intro:
      "Simple explanation of the four things Stockaily shows on every analysis.",
    education_score_title: "AI Opportunity Score (0–100)",
    education_score_body:
      "Combines valuation, business quality, growth and risk into one 0–100 signal. Higher is not automatically 'buy' — it means the stock currently looks more attractive versus its estimated fair value and risk profile.",
    education_fairvalue_title: "Fair Value",
    education_fairvalue_body:
      "Stockaily's estimated intrinsic value per share, blended across valuation methods (DCF, sector P/E, book value). Compare it to the current price to see upside/downside.",
    education_risk_title: "Risk Level",
    education_risk_body:
      "A Low / Medium / High rating based on beta, financial health and fair-value confidence. Higher risk stocks may deserve smaller position sizes.",
    concept_fair_value: "Fair Value",
    concept_fair_value_help:
      "Estimated intrinsic value per share. Blended from DCF, sector P/E and Price/Book depending on the stock type.",
    concept_score: "AI Opportunity Score",
    concept_score_help:
      "0–100 composite of valuation, business quality, growth and risk. Higher means more attractive vs fair value + risk profile.",
    concept_risk: "Risk Level",
    concept_risk_help:
      "Low / Medium / High. Considers beta, financial health, and confidence in the fair-value estimate.",
    concept_bullbear: "Bull / Bear Case",
    concept_bullbear_help:
      "AI-generated arguments for and against owning this stock, grounded in the underlying valuation and metrics.",
    concept_drivers: "Key Drivers",
    concept_drivers_help:
      "The specific metrics (cash flow yield, ROIC, revenue growth, P/E vs sector, debt-to-equity, fair value confidence) that most influenced the score.",
  },
  activity: {
    // NOTE: legacy `activity.*` keys were removed with the Hero
    // ActivityBar (Landing Polish — capability strip replaced the
    // fabricated live-stats chip). Keys are kept intentionally empty
    // to avoid breaking any external consumer that may still be using
    // the namespace; new UI must NOT reintroduce these keys.
  },
  landing: {
    hero_title_l1: "See a stock\u2019s estimated Fair Value",
    hero_title_l2: "in under 30 seconds.",
    hero_subtitle: "Fair Value, Opportunity Scores and portfolio intelligence for serious retail investors. Backed by fundamentals and a transparent methodology, not black-box AI.",
    hero_search_placeholder: "Search any stock ticker (try AAPL, NVDA, GOOGL)",
    hero_cta: "Analyze your first stock",
    capability_primary: "Thousands of stocks",
    capability_secondary: "AI-assisted analysis across multiple valuation models",
    capability_group_aria: "Stockaily capabilities",
    how_it_works_eyebrow: "HOW IT WORKS",
    how_it_works_title: "From stock search to a clearer investment thesis",
    how_it_works_subtitle: "Understand valuation, key drivers, and risks in three focused steps.",
    how_it_works_step_find_title: "Find a stock",
    how_it_works_step_find_desc: "Search thousands of supported stocks and select the company you want to understand.",
    how_it_works_step_find_cat: "SEARCH",
    how_it_works_step_value_title: "Understand the valuation",
    how_it_works_step_value_desc: "See the current price, estimated Fair Value, upside or downside, and AI Opportunity Score at a glance.",
    how_it_works_step_value_cat: "VALUATION",
    how_it_works_step_conviction_title: "Build your conviction",
    how_it_works_step_conviction_desc: "See the bull case, bear case, key risks, and the drivers that matter most.",
    how_it_works_step_conviction_cat: "THESIS",
    // Landing Polish 2 — Step 2 (Valuation) curated PayPal illustration
    hiw_step2_example_marker: "Example analysis",
    hiw_step2_score_label: "AI Opportunity Score",
    hiw_step2_signal_label: "Opportunity Level \u00B7 High",
    hiw_step2_assessment: "Undervalued by 29.8%",
    hiw_step2_chip_valuation: "Valuation \u00B7 Undervalued",
    hiw_step2_chip_risk: "Risk Level \u00B7 Low",
    hiw_step2_current_price_label: "Current Price",
    hiw_step2_now_label: "Now",
    hiw_step2_fair_value_label: "Fair Value",
    hiw_step2_range_label: "range",
    hiw_step2_status_upside: "Upside",
    hiw_step2_status_risk: "Risk",
    hiw_step2_status_horizon: "Horizon",
    hiw_step2_risk_low: "Low",
    hiw_step2_graph_now_label: "NOW",
    hiw_step2_graph_fair_value_label: "FAIR VALUE",
    hiw_step2_graph_aria: "Illustrative comparison of PayPal's current price versus its estimated Fair Value, showing an upside gap.",
    // Landing Polish 2 — Step 3 (Thesis) categories + micro examples
    hiw_step3_bull_case: "Bull case",
    hiw_step3_bear_case: "Bear case",
    hiw_step3_key_risks: "Key risks",
    hiw_step3_key_drivers: "Key drivers",
    hiw_step3_bull_case_example: "Strong cash generation",
    hiw_step3_bear_case_example: "Slower growth",
    hiw_step3_key_risks_example: "Competitive pressure",
    hiw_step3_key_drivers_example: "Margins & free cash flow",
    how_it_works_cta: "Analyze a stock",
    // Landing 1 (2026-08-10) — subtle single-line metadata beneath
    // every AI Market Snapshot card. Replaces the previous three
    // lines (Data as of / Analysis updated / Model: <internal id>).
    // The Snapshot is explicitly an "example preview" and no date
    // is exposed here to avoid implying continuously current data.
    pick_model_example_preview: "Model: Example preview",
    pick_data_as_of: "Data as of: {date}",
    pick_analysis_updated: "Analysis updated: {date}",
    pick_model: "Model: {model}",
    pick_snapshot_published: "Snapshot published: {date}",
    // Landing 1 correction (2026-08-11) — minimal, explicit,
    // non-chart unavailable state. Never rendered when legitimate
    // cached series exist; visually minimal and clearly a text
    // status, NOT a decorative pseudo-chart.
    pick_chart_unavailable: "Chart unavailable",
    picks_eyebrow: "AI ANALYSIS \u00B7 EXAMPLES",
    picks_title: "AI Market Snapshot",
    picks_subtitle: "Explore example analyses across undervalued, fairly valued, and overvalued stocks.",
    picks_as_of: "As of {time}",
    picks_refresh: "Refresh",
    legend_balanced: "Balanced overview \u00B7 not all stocks shown are buy opportunities.",
    legend_opportunity: "Opportunity",
    legend_opportunity_desc: "Trading below fair value",
    legend_neutral: "Neutral",
    legend_neutral_desc: "Fairly valued",
    legend_risk: "Risk",
    legend_risk_desc: "Priced ahead of fundamentals",
    picks_disclaimer: "A balanced overview \u2014 at least 2 opportunities, 1 fairly valued and 1 risk to give a realistic market picture. Not a buy/sell recommendation.",
    trust_cashflow_title: "Cash flow",
    trust_cashflow_sub: "Cash flow strength and consistency",
    trust_valuation_title: "Valuation",
    trust_valuation_sub: "Fair Value based on company and sector data",
    trust_profitability_title: "Profitability",
    trust_profitability_sub: "Returns, margins & earnings quality",
    trust_ai_title: "AI insight",
    trust_ai_sub: "Clear insights based on the analysis",
    trust_footnote: "Every score is built on real fundamentals \u2014 no black-box magic.",
    trust_footnote_line1: "Every score is built on real fundamentals",
    trust_footnote_line2: "\u2014 no black-box magic.",
    cta_title: "Stop guessing. Start analyzing.",
    cta_subtitle: "Free for your first 10 analyses. No credit card required.",
    cta_signup: "Create free account",
    // Sub-pass 2A (2026-02-17) — the FINAL landing CTA for logged-out
    // visitors is now aligned with the hero CTA ("Analyze your first
    // stock") both in copy AND primary destination.  The `hero_cta`
    // key stays the source of truth; this alias exists so future
    // copy tweaks can diverge without touching the hero.
    cta_signup_final: "Analyze your first stock",
    // Sub-pass 2A (2026-02-17) — Opportunity Score scale on Market
    // Snapshot cards.  Bands mirror the canonical thresholds in
    // poc/valuation_guards.py.
    snapshot_score_scale_disclosure: "Final classification also considers valuation, upside, risk and deterministic guardrails.",
    snapshot_score_scale_aria: "AI Opportunity Score {score} of 100 \u2014 score-only band: {band}.",
    snapshot_score_scale_band_risk: "Risk (below 45)",
    snapshot_score_scale_band_neutral: "Neutral (45-59)",
    snapshot_score_scale_band_opportunity: "Opportunity threshold (60-74)",
    snapshot_score_scale_band_top: "Top Opportunity threshold (75-100)",
    cta_pricing: "See pricing",
    // Landing Page Polish 3 — bilingual FAQ (8 questions).
    faq_eyebrow: "FAQ",
    faq_title: "Questions, answered.",
    faq_subtitle: "Everything you need to know before your first Stockaily analysis.",
    faq_q1_q: "Can I try Stockaily for free?",
    faq_q1_a: "Yes. You receive ten free stock analyses during your first month, with no credit card required. After that, Free members continue to receive five free stock analyses per month.",
    faq_q2_q: "Which stocks can I analyze?",
    faq_q2_a: "Stockaily supports thousands of listed stocks across multiple markets. Coverage depends on the financial data required for a reliable analysis. If important inputs cannot be validated, Stockaily shows the analysis or valuation as unavailable instead of presenting an unverified result. ETFs and ETNs are not currently supported.",
    faq_q3_q: "What does the AI Opportunity Score mean?",
    faq_q3_a: "The AI Opportunity Score summarizes valuation, business quality, growth, and risk and safety factors on a scale from 0 to 100. It helps you compare the overall analytical setup of a stock, but it is not a buy, sell, or hold recommendation.",
    faq_q4_q: "How does Stockaily estimate Fair Value?",
    faq_q4_a: "Stockaily combines suitable valuation methods based on the company and available data, including discounted cash flow and sector-relative approaches. The result is an estimated Fair Value range, not a guaranteed target price. Valuation assumptions and market conditions can change.",
    faq_q5_q: "How current and reliable is the data?",
    faq_q5_a: "Stockaily uses market and fundamental data from external providers and displays the relevant data and update dates where available. Data may be delayed, incomplete, revised, or temporarily unavailable. Stockaily uses validation and reconciliation checks and withholds valuation outputs when essential inputs cannot be verified.",
    faq_q6_q: "What role does AI play in the analysis?",
    faq_q6_a: "Stockaily's valuation model calculates the core scores and valuation outputs from structured financial data. AI-assisted text explains those model outputs and highlights relevant opportunities and risks, but it does not independently override the calculated valuation.",
    faq_q7_q: "Is Stockaily investment advice?",
    faq_q7_a: "No. Stockaily provides model-based financial research and information for your own evaluation. It does not consider your personal financial situation and does not provide personalized investment advice, order execution, position-size recommendations, or guaranteed returns. Investing involves risk.",
    faq_q8_q: "What is included in the paid plans, and can I cancel?",
    faq_q8_a: "Pro and Premium provide higher or unlimited usage limits and additional analysis, watchlist, and portfolio features depending on the selected plan. Current features and prices are listed on the Pricing page. Paid subscriptions can be cancelled at any time and remain available until the end of the paid billing period, subject to the applicable terms.",
    faq_q8_pricing_link_text: "Pricing page",
    // Landing Page Polish 4 — Portfolio Showcase / Preview Loop.
    showcase_eyebrow: "PORTFOLIO INTELLIGENCE",
    showcase_heading: "See your whole portfolio more clearly.",
    showcase_subtitle: "Track performance, valuation, opportunities and risk \u2014 then turn your holdings into a focused research plan.",
    showcase_example_badge: "Example portfolio \u00B7 Illustrative data",
    showcase_disclosure: "Illustrative interface and data \u2014 not actual portfolio performance or investment advice.",
    showcase_play: "Play showcase",
    showcase_pause: "Pause showcase",
    showcase_reduced_static: "Static preview \u2014 reduced motion enabled.",
    showcase_header_title: "Portfolio Intelligence",
    showcase_header_subtitle: "Track your holdings against Stockaily's Fair Value, opportunity score, and risk framework.",
    showcase_refresh_prices: "Refresh Prices",
    showcase_analyze_portfolio: "Analyze Portfolio",
    showcase_analyze_running: "Analyzing\u2026",
    showcase_update_analysis: "Update Portfolio Analysis",
    showcase_add_holding: "Add Holding",
    showcase_premium_ai: "PREMIUM AI",
    showcase_loading_msg: "Running the Buffett lens on your holdings\u2026",
    // KPIs
    showcase_kpi_value: "Portfolio Value",
    showcase_kpi_value_hint: "Cost basis: $25,000",
    showcase_kpi_fv: "Estimated Fair Value",
    showcase_kpi_upside: "Upside / Downside",
    showcase_kpi_upside_hint: "Estimated vs current portfolio value",
    showcase_kpi_pnl: "Unrealized P/L",
    showcase_kpi_score: "Weighted Opportunity Score",
    showcase_kpi_score_hint: "Value-weighted across holdings",
    showcase_kpi_risk: "Risk Level",
    showcase_risk_medium: "Medium",
    // Growth chart
    showcase_growth_title: "Portfolio Growth",
    showcase_growth_sub: "Value vs estimated fair value",
    showcase_time_weighted: "Time-weighted return",
    showcase_good: "GOOD",
    showcase_portfolio_return: "PORTFOLIO RETURN",
    showcase_sp500_return: "S&P 500 RETURN",
    showcase_relative_perf: "RELATIVE PERFORMANCE",
    showcase_axis_perf: "Performance index",
    showcase_axis_fv: "Estimated Fair Value ($)",
    showcase_legend_fv: "Estimated Fair Value",
    showcase_legend_portfolio: "Portfolio Value",
    showcase_legend_sp500: "S&P 500 Benchmark (Index)",
    showcase_tooltip_date: "DATE:",
    showcase_tooltip_portfolio_index: "Portfolio Index",
    showcase_tooltip_portfolio_value: "Portfolio Value",
    showcase_tooltip_sp500_index: "S&P 500 Index",
    showcase_tooltip_est_fv: "Est. Fair Value",
    showcase_tooltip_portfolio_since: "Portfolio since 1Y start",
    showcase_tooltip_sp500_since: "S&P 500 since 1Y start",
    showcase_tooltip_relative: "Relative Performance",
    // Insight cards
    showcase_top_opportunity: "TOP OPPORTUNITY",
    showcase_top_opportunity_desc: "Position with the largest upside vs estimated fair value.",
    showcase_largest_risk: "LARGEST VALUATION RISK",
    showcase_largest_risk_desc: "Position trading furthest above estimated fair value.",
    showcase_highest_weight: "HIGHEST WEIGHT",
    showcase_highest_weight_desc: "Position with the largest portfolio share.",
    showcase_biggest_winner: "BIGGEST WINNER",
    showcase_biggest_winner_desc: "Strongest unrealized return.",
    showcase_biggest_loser: "BIGGEST LOSER",
    showcase_biggest_loser_desc: "Largest unrealized loss.",
    showcase_concentration: "CONCENTRATION",
    showcase_concentration_desc: "Financials sector share of portfolio.",
    // Holdings table
    showcase_col_ticker: "Ticker",
    showcase_col_company: "Company",
    showcase_col_shares: "Shares",
    showcase_col_buy: "Buy Price",
    showcase_col_current: "Current",
    showcase_col_mv: "Market Value",
    showcase_col_pnl: "P/L",
    showcase_col_weight: "Weight",
    showcase_col_fv: "Fair Value",
    showcase_col_upside: "Upside",
    showcase_col_score: "AI Score",
    showcase_col_risk: "Risk",
    showcase_risk_low: "Low",
    showcase_risk_med: "Medium",
    showcase_risk_high: "High",
    showcase_signal_top_opportunity: "Top Opportunity",
    showcase_signal_opportunity: "Opportunity",
    showcase_signal_fair: "Fair",
    showcase_signal_valuation_risk: "Valuation Risk",
    // AI Portfolio Review
    showcase_review_title: "AI Portfolio Review",
    showcase_review_badge: "Portfolio Intelligence \u00B7 AI Assisted",
    showcase_review_badge_example: "Example",
    showcase_review_up_to_date: "Up to date",
    showcase_review_summary:
      "The portfolio combines positive performance with several holdings trading below estimated Fair Value. Opportunities are distributed across multiple sectors, while valuation and sector concentration still require attention.",
    showcase_review_strengths: "STRENGTHS",
    showcase_review_weaknesses: "WEAKNESSES",
    showcase_review_concentration: "CONCENTRATION RISKS",
    showcase_review_priorities: "RESEARCH PRIORITIES",
    showcase_review_confidence:
      "Confidence: Illustrative example based on the shown holdings and fundamentals; not a personal recommendation.",
    showcase_review_strengths_1: "Several holdings trade below estimated Fair Value.",
    showcase_review_strengths_2: "The portfolio shows positive overall performance.",
    showcase_review_strengths_3: "Exposure is spread across five sectors.",
    showcase_review_weaknesses_1: "Apple trades above its illustrative estimated Fair Value.",
    showcase_review_weaknesses_2: "Not every holding currently offers the same opportunity potential.",
    showcase_review_concentration_1: "Financials represent 34% of the portfolio and deserve continued monitoring.",
    showcase_review_priorities_1: "Review PayPal's cash-flow development.",
    showcase_review_priorities_2: "Monitor NextEra Energy's capital requirements.",
    showcase_review_priorities_3: "Reassess Apple's valuation assumptions.",
    // Profit / Loss Breakdown
    showcase_pl_breakdown_title: "PROFIT / LOSS BREAKDOWN",
    showcase_pl_unrealized: "Unrealized P/L",
    showcase_pl_realized: "Realized P/L",
    showcase_pl_total: "Total P/L",
  },
  pricing: {
    pill: "Simple, transparent pricing",
    title_main: "Find your edge.",
    title_sub: "Invest with more conviction.",
    subtitle: "Start with core valuations. Unlock complete stock research with Pro. Understand your entire portfolio with Premium.",
    monthly: "Monthly",
    yearly: "Yearly",
    most_popular: "Most popular",
    premium_tier: "Premium tier",
    no_card: "No card required.",
    switch_to_yearly: "Switch to yearly and save \u20AC{amount} per year",
    compare_features: "Compare features",
    trust_cancel_title: "Cancel anytime",
    trust_cancel_body: "No long-term commitment. Switch back to Free at any time.",
    trust_eu_title: "EU billing & VAT",
    trust_eu_body: "Invoices include VAT where applicable, sent automatically to your inbox.",
    trust_nospam_title: "No spam, no popups",
    trust_nospam_body: "We don't sell your data and we won't interrupt your workflow with sales overlays.",
    plans: {
      free_tagline: "Explore Stockaily with full core valuations.",
      pro_tagline: "Complete stock research for active investors.",
      premium_tagline: "Portfolio intelligence across every holding.",
      free_cta: "Get started",
      pro_cta: "Upgrade to Pro",
      premium_cta: "Upgrade to Premium",
    },
    feature_groups: {
      "Core": "Core",
      "Complete stock research": "Complete stock research",
      "Everything in Pro, plus": "Everything in Pro, plus",
      "Coming next": "Coming next",
      // Legacy keys kept for backward compatibility with cached backend responses.
      "Limitations": "Limitations",
      "Everything you need": "Everything you need",
      "Coming soon": "Coming soon",
    },
    features: {
      // Free — current copy
      "10 analyses in your first 30 days": "10 analyses in your first 30 days",
      "5 analyses every rolling 30 days after that": "5 analyses every rolling 30 days after that",
      "AI Opportunity Score": "AI Opportunity Score",
      "Fair Value & upside/downside": "Fair Value & upside/downside",
      "Risk level": "Risk level",
      "Basic AI summary": "Basic AI summary",
      "Up to 10 watchlist stocks": "Up to 10 watchlist stocks",
      // Pro — current copy
      "Unlimited stock analyses": "Unlimited stock analyses",
      "Full AI Insights": "Full AI Insights",
      "Bull & Bear Case": "Bull & Bear Case",
      "Detailed risk analysis": "Detailed risk analysis",
      "Why This Score": "Why This Score",
      "Sector comparison": "Sector comparison",
      "Unlimited watchlist": "Unlimited watchlist",
      "Notes, labels & categories": "Notes, labels & categories",
      "KPI explanations & tooltips": "KPI explanations & tooltips",
      "Email alerts for price and fair-value changes": "Email alerts for price and fair-value changes",
      // Premium — current copy
      "Portfolio tracking": "Portfolio tracking",
      "Buy-price & position performance": "Buy-price & position performance",
      "Realized and unrealized P/L": "Realized and unrealized P/L",
      "Closed positions": "Closed positions",
      "Portfolio Growth dashboard": "Portfolio Growth dashboard",
      "Portfolio Fair Value": "Portfolio Fair Value",
      "Weighted Opportunity Score & risk": "Weighted Opportunity Score & risk",
      "Portfolio AI Review": "Portfolio AI Review",
      "S&P 500 benchmark": "S&P 500 benchmark",
      "Portfolio refresh": "Portfolio refresh",
      "Premium PDF reports": "Premium PDF reports",
      "CSV export": "CSV export",
      // Phase 8.PH.FINAL — legacy labels kept for backward compat only.
      "CSV / XLSX exports": "CSV export",
      "Advanced portfolio analytics": "Advanced portfolio analytics",
      // Legacy keys retained for backward compatibility with cached
      // backend responses. Safe to remove after a full cache-flush cycle.
      "10 analyses in first 30 days": "10 analyses in your first 30 days",
      "5 analyses per rolling 30 days": "5 analyses every rolling 30 days after that",
      "Basic valuation access": "Fair Value & upside/downside",
      "Up to 10 watchlist tickers": "Up to 10 watchlist stocks",
      "No portfolio tracking": "No portfolio tracking",
      "Limited AI reasoning": "Limited AI reasoning",
      "Unlimited analyses": "Unlimited stock analyses",
      "Full AI insights": "Full AI Insights",
      "Unlimited watchlist items": "Unlimited watchlist",
      "Watchlist notes": "Notes, labels & categories",
      "Watchlist labels & categories": "Notes, labels & categories",
      "AI risk analysis": "Detailed risk analysis",
      "Bull / Bear case analysis": "Bull & Bear Case",
      "Email alerts on price & fair-value crossings": "Email alerts for price and fair-value changes",
      "Portfolio growth dashboard": "Portfolio Growth dashboard",
      "Buy-price tracking & position performance": "Buy-price & position performance",
      "Portfolio AI analysis": "Portfolio AI Review",
      "Portfolio fair value & opportunity scoring": "Portfolio Fair Value",
      "S&P 500 benchmark (coming soon)": "S&P 500 benchmark",
      "PDF reports (coming soon)": "Premium PDF reports",
      "CSV / XLSX export (coming soon)": "CSV / XLSX exports",
      "Advanced stock screener": "Advanced portfolio analytics",
      "Data export (CSV / XLSX)": "CSV / XLSX exports",
      "Future advanced analytics modules": "Advanced portfolio analytics",
    },
    compare: {
      group_analyses: "Analyses & AI",
      group_watchlist: "Watchlist",
      group_portfolio: "Portfolio intelligence",
      row_analyses: "Analyses",
      row_ai_score: "AI Opportunity Score",
      row_fair_value: "Fair Value & upside/downside",
      row_ai: "AI insights",
      row_ai_risk: "Detailed risk analysis",
      row_bull_bear: "Bull & Bear Case",
      row_why_score: "Why This Score",
      row_sector: "Sector comparison",
      row_kpi: "KPI explanations & tooltips",
      row_wl_count: "Watchlist stocks",
      row_notes: "Notes",
      row_labels: "Labels & categories",
      row_alerts: "Email alerts",
      row_portfolio: "Portfolio tracking",
      row_buy_price: "Buy-price tracking",
      row_position_performance: "Position performance",
      row_unrealized_pl: "Unrealized P/L",
      row_realized_pl: "Realized P/L",
      row_closed_positions: "Closed positions",
      row_portfolio_fair_value: "Portfolio Fair Value",
      row_weighted_opp: "Weighted Opportunity Score",
      row_portfolio_risk: "Portfolio risk overview",
      row_portfolio_ai_review: "Portfolio AI Review",
      row_dashboard: "Portfolio Growth dashboard",
      row_sp500: "S&P 500 benchmark",
      row_portfolio_refresh: "Portfolio refresh",
      row_pdf_export: "Premium PDF reports",
      row_export: "CSV export",
      row_advanced_analytics: "Advanced portfolio analytics",
      // Legacy row keys kept for cached copies.
      row_position: "Buy-price & position performance",
      row_ai_portfolio: "Portfolio AI Review",
      row_screener: "Advanced portfolio analytics",
      // Value labels
      val_free_analyses: "10 first 30d \u00B7 then 5 / 30d",
      val_ai_basic: "Basic summary",
      val_ai_full: "Full AI Insights",
      val_risk_basic: "Basic risk level",
      val_limited: "Limited",
      val_up_to_10: "Up to 10",
      val_unlimited: "Unlimited",
      val_coming_soon: "Coming soon",
      // Legacy value keys
      val_basic_preview: "Basic summary",
      val_full_reasoning: "Full AI Insights",
    },
  },
  watchlist: {
    title: "Watchlist",
    subtitle: "Save stocks here to track price, fair value and AI score at a glance.",
    add_placeholder: "Add a ticker",
    free_quota: "Free plan watchlist",
    at_limit: "You've hit the free plan limit. Upgrade to Pro for unlimited watchlists and AI tracking.",
    add_more: "Add {n} more stock on the Free plan, or go Pro for unlimited.",
    add_more_plural: "Add {n} more stocks on the Free plan, or go Pro for unlimited.",
    unlimited_chip: "Unlimited watchlist on your {plan} plan",
    failed_load: "Failed to load watchlist",
    failed_remove: "Failed to remove",
    removed: "Removed {ticker}",
    saved: "Saved notes for {ticker}",
    failed_save: "Failed to save",
    col_ticker: "Ticker",
    col_name: "Name",
    col_notes: "Notes & tags",
    col_price: "Price",
    col_price_as_of_analysis: "as of analysis {time}",
    col_price_tooltip: "Price shown is from the saved analysis snapshot ({time}), not a live quote.",
    stale_badge: "Stale",
    stale_label: "Last analysis {time} · stale",
    stale_label_fresh: "Last analysis {time}",
    stale_tooltip: "This analysis is more than 24 hours old. Re-analyse the ticker to refresh Price, Fair Value and Upside together.",
    col_fair: "Fair value",
    col_upside: "Upside",
    col_score: "Score",
    col_status: "Status",
    status_not_analyzed_yet: "Not analyzed yet",
    col_updated: "Updated",
    col_actions: "Actions",
    edit_title_pro: "Edit notes, labels & sector",
    edit_title_locked: "Available on Pro",
    btn_analyze: "Analyze",
    no_notes: "No notes yet",
    pro_feature: "Pro feature",
    notes_label: "Notes",
    notes_placeholder: "Why are you watching {ticker}? Earnings setup, target entry, exit thesis\u2026",
    labels_label: "Labels",
    labels_add_placeholder: "Add custom label\u2026",
    sector_label: "Sector & themes",
    pro_toast_title: "Notes & labels are on Pro",
    pro_toast_desc: "Tag stocks by thesis, leave notes per analysis, group by sector. Available on Pro and Premium.",
    pro_toast_action: "Upgrade",
    teaser_title: "Notes, labels & sector tags are on Pro",
    teaser_body: "Tag stocks by thesis (AI \uD83E\uDD16, Semis \uD83D\uDCBE, Space \uD83D\uDE80\u2026) and leave notes after each analysis.",
    add_success: "{ticker} added to your Watchlist.",
    add_success_analyze: "Analyze now",
    add_already: "Already in your Watchlist.",
    add_error: "Could not add to Watchlist.",
    add_invalid: "This ticker could not be added.",
    limit_toast: "Watchlist limit reached.",
    add_placeholder_desktop: "Add stock by ticker or company",
    add_a11y_label: "Add a stock by ticker or company name",
    loggedout_title: "Build your Stockaily Watchlist",
    loggedout_subtitle: "Track price, Fair Value and AI Opportunity Scores in one place.",
    loggedout_signup: "Create free account",
    loggedout_login: "Log in",
    analysis_section_kicker: "DEEPER ANALYSIS",
    analysis_section_title: "Analyze any stock",
    analysis_section_body: "Get Fair Value, Opportunity Score, risk and AI insights in seconds.",
    analysis_capabilities: "Fair Value \u00B7 Opportunity Score \u00B7 Risk \u00B7 AI Insights",
    analysis_placeholder: "Search a stock to analyze",
    analysis_placeholder_mobile: "Analyze a stock",
    empty_title: "Nothing saved yet",
    empty_subtitle: "Add your first stock to track price, Fair Value and AI Opportunity Score.",
    empty_cta: "Add your first stock",
    empty_secondary: "Analyze a stock instead",
    sort: {
      button: "Sort",
      header: "Sort by",
      asc: "Asc",
      desc: "Desc",
      recently_added: "Recently added",
      ticker: "Ticker",
      name: "Name",
      price: "Price",
      fair_value: "Fair value",
      upside: "Upside",
      ai_score: "AI Score",
      status: "Status",
      last_updated: "Last updated",
      reset_default: "Reset to default",
    },
    filter: {
      button: "Filters",
      sheet_desc: "Filter your Watchlist by valuation, labels or sectors.",
      valuation_status: "Valuation status",
      status_undervalued: "Undervalued",
      status_fair: "Fairly valued",
      status_overvalued: "Overvalued",
      labels: "Labels",
      sectors: "Sector & themes",
      reset: "Reset filters",
      active_count: "Active filters:",
      hint_no_meta: "Add labels or sector tags in the row editor to unlock more filters.",
      empty_title: "No stocks match your filters",
      empty_subtitle: "{hidden} stock(s) hidden by filters.",
    },
    freshness: {
      latest_data: "Latest data",
      latest_available_data: "Latest available data",
      updated_ago: "{age}",
      unavailable: "Update time unavailable",
      unavailable_short: "—",
    },
    sector: {
      show_more: "Show more",
      more_header: "More sectors & themes",
      mobile_desc: "Choose up to 3 tags.",
      count_of_max: "{n} / {max} selected",
      max_reached: "Max 3 sectors",
      max_reached_hint: "Max 3 — deselect one to add another",
    },
    suggested_labels: {
      "Long-term": "Long-term",
      "Earnings Watch": "Earnings Watch",
      "Risk Flag": "Risk Flag",
      "High Conviction": "High Conviction",
      "Dividend Candidate": "Dividend Candidate",
      "Research": "Research",
      "Watching": "Watching",
      "Earnings play": "Earnings play",
      "Dividend": "Dividend",
      "Speculative": "Speculative",
      "Add on dip": "Add on dip",
      "Trim on rally": "Trim on rally",
      "Thesis intact": "Thesis intact",
      "Risk flag": "Risk flag",
    },
    sectors: {
      ai: "AI",
      semis: "Semis",
      software: "Software",
      cloud: "Cloud",
      cybersec: "Cybersecurity",
      banks: "Banks",
      fintech: "Fintech",
      insurance: "Insurance",
      finance: "Finance",
      healthcare: "Healthcare",
      biotech: "Biotech",
      pharma: "Pharma",
      energy: "Energy",
      clean_energy: "Clean Energy",
      industrial: "Industrial",
      aerospace: "Aerospace",
      auto: "Auto / EV",
      consumer: "Consumer",
      media: "Media",
      telecom: "Telecom",
      reits: "REITs",
      materials: "Materials",
      utilities: "Utilities",
      space: "Space",
      crypto: "Crypto",
    },
  },
  auth: {
    welcome_back: "Welcome back.",
    welcome_back_sub: "Sign in to access your watchlist and continue running AI valuations on stocks you follow.",
    login_title: "Log in",
    login_subtitle: "Use your email or sign in with Google.",
    google: "Continue with Google",
    email_label: "Email",
    email_placeholder: "you@stockaily.ai",
    password_label: "Password",
    password_placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
    name_label: "Name",
    name_placeholder: "Your name",
    signing_in: "Signing in\u2026",
    log_in: "Log in",
    no_account: "No account yet?",
    create_one: "Create one",
    forgot_password_link: "Forgot password?",
    welcome_toast: "Welcome back to Stockaily.",
    bad_creds: "Invalid email or password",
    signup_kicker: "Find undervalued stocks in seconds.",
    signup_kicker_sub: "Free plan starts with 10 analyses, then 5 per rolling 30 days. Upgrade anytime.",
    signup_title: "Create your account",
    signup_subtitle: "Free to start. No credit card needed.",
    signup_password_placeholder: "At least 6 characters",
    creating: "Creating\u2026",
    create_account: "Create account",
    have_account: "Already have an account?",
    welcome_signup_toast: "Welcome to Stockaily.",
    signup_failed: "Sign up failed",
    password_too_short: "Password must be at least 6 characters",
  },
  billing: {
    title: "Billing & usage",
    subtitle: "Manage your plan and track your remaining analyses.",
    plan_label: "Plan",
    billed_label: "Billed",
    analyses_used: "Analyses used",
    unlimited_msg: "Unlimited \u2014 enjoy your {plan} plan.",
    used_in_first: "{used} / {limit} this first 30 days",
    used_in_rolling: "{used} / {limit} this rolling {days} days",
    remaining: "{remaining} analyses remaining",
    choose_plan: "Choose your plan",
    recommended: "Recommended",
    redirect_msg: "Redirecting to checkout (CopeCart placeholder)\u2026",
    switch_free: "Switch to Free",
    checkout_yearly: "Checkout yearly",
    checkout_monthly: "Checkout monthly",
    activate_test: "Activate (test)",
    plan_switched: "Plan switched to {plan}",
    failed_update: "Failed to update plan",
    checkout_failed: "Checkout failed",
    disclaimer: "CopeCart checkout is configured but not publicly enabled yet.",
    disclaimer_enabled: "Secure checkout is processed by CopeCart.",
    disclaimer_disabled: "CopeCart checkout is configured but not publicly enabled yet.",
    // Phase 7.UI.15k — continuation page after auth.
    continue: {
      preparing_title: "Preparing your secure checkout\u2026",
      preparing_body: "We\u2019re sending you to CopeCart with your selected plan. This only takes a moment.",
      ready_title: "Your checkout is ready",
      ready_body: "If your browser didn\u2019t open CopeCart automatically, use the button below to continue.",
      cta: "Continue to secure checkout",
      cancel: "Cancel and return to Billing",
      error_title: "We couldn\u2019t prepare checkout",
      error_disabled: "Checkout isn\u2019t publicly available yet. Please try again later or contact support.",
      error_generic: "Something went wrong while preparing your checkout. You can pick a plan again from the Billing page.",
      retry: "Try again",
      back_to_billing: "Back to Billing",
    },
    success_charged_by_copecart: "The charge is processed by CopeCart.",
    // Phase 7.UI.15i — CopeCart subscription management
    copecart: {
      title: "Manage your CopeCart subscription",
      subtitle: "Your subscription is processed and billed by CopeCart. Manage cancellation, refunds and invoices directly on CopeCart.",
      current_plan: "Current plan",
      billing_interval: "Billing interval",
      subscription_status: "Subscription status",
      access_until: "Access valid until",
      next_renewal: "Next renewal",
      payment_provider: "Payment provider",
      order_id: "Order ID",
      transaction_id: "Transaction ID",
      subscription_id: "Subscription ID",
      purchase_email: "Purchase email",
      cancel_button: "Manage or cancel subscription on CopeCart",
      cancel_helper: "CopeCart handles subscription management, cancellation, withdrawal requests, and invoices. Use the purchase email and Order ID shown above.",
      cancel_safety: "Do not use Switch to Free to cancel a CopeCart subscription. Your subscription is managed by CopeCart. Stockaily updates your plan automatically after CopeCart confirms cancellation or expiry.",
      cancelled_notice: "Cancellation scheduled. Your access remains active until {date}.",
      past_due_notice: "Payment past due. Please update your payment method on CopeCart to keep access.",
      invoices_title: "Billing history & invoices",
      invoices_note: "Your invoices and payment confirmations are sent directly by CopeCart to your purchase email.",
      latest_payment: "Latest payment",
      test_badge: "Test payment",
      test_notice: "This is a CopeCart sandbox/test purchase. It is excluded from billing metrics but you can still test the cancellation flow.",
      copy_label: "Copy",
      copied_label: "Copied",
      switch_free_disabled_title: "Switch to Free disabled",
      switch_free_disabled_body: "You have an active CopeCart subscription. Use \u201CCancel subscription on CopeCart\u201D above to stop billing. Stockaily updates your plan automatically once CopeCart confirms.",
    },
    // Billing Cancellation Flow Repair — restored explanatory section.
    // Renders for every user (free, CopeCart, manual, admin, promo) so
    // the Billing page always explains where billing lives and how
    // cancellation works.
    how_it_works: {
      title: "How your subscription works",
      current_plan_row: "Current plan",
      billing_interval_row: "Billing interval",
      allowance_row: "Analyses allowance",
      billed_by_row: "Billed by",
      cancellation_row: "How to cancel",
      access_until_row: "Access valid until",
      allowance_unlimited: "Unlimited",
      allowance_count: "{used} of {limit} used ({remaining} remaining)",
      allowance_free_first_30d: "{used} of {limit} in your first 30 days",
      billed_by_copecart: "CopeCart",
      billed_by_manual: "Manually granted \u2014 not billed",
      billed_by_admin: "Admin-granted \u2014 not billed",
      billed_by_promo: "Promotional access \u2014 not billed",
      billed_by_none: "No active paid subscription",
      cancellation_copecart: "Manage or cancel on CopeCart. Paid access remains active until the cancellation / renewal end date.",
      cancellation_manual: "Contact {email} to change or cancel your plan.",
      cancellation_free: "You are on the Free plan. Nothing to cancel.",
      billing_interval_none: "\u2014",
    },
    manage: {
      cta_copecart: "Manage or cancel on CopeCart",
      cta_support: "Contact support",
      loading: "Checking your subscription\u2026",
      error_not_configured_title: "Cancellation link unavailable",
      error_not_configured_body: "The CopeCart management link is not configured yet. Please contact {email} to cancel your subscription \u2014 we will handle it manually and confirm by email.",
      error_generic_title: "We couldn\u2019t open cancellation",
      error_generic_body: "Something went wrong while resolving the cancellation link. Please try again in a moment or contact {email}.",
      confirm_dialog_title: "Cancellation is completed on CopeCart",
      confirm_dialog_body: "Stockaily does not cancel your subscription itself. We will open CopeCart in a new tab so you can cancel there. Your Stockaily access remains active until the cancellation / renewal end date, and updates automatically once CopeCart confirms.",
      confirm_dialog_confirm: "Continue to CopeCart",
      confirm_dialog_cancel: "Keep my subscription",
      support_dialog_title: "Contact support to change your plan",
      support_dialog_body: "Your plan was granted outside of CopeCart, so there is nothing to cancel there. Please contact {email} to change or cancel it. We do not automatically downgrade manually-granted plans.",
      support_dialog_confirm: "Open email to support",
      support_dialog_cancel: "Close",
      already_free_toast: "You are already on the Free plan.",
    },
  },
  paywall: {
    title: "Unlock Stockaily Pro",
    default_reason: "You\u2019ve used your free analyses for this period. Upgrade for unlimited analyses, full AI insights, and watchlist alerts.",
    bullet_unlimited: "Unlimited stock analyses",
    bullet_ai: "Full AI insights & summary",
    bullet_alerts: "Watchlist & saved analyses",
    later: "Later",
    see_plans: "See plans",
  },
  not_found: {
    title: "404",
    body: "Page not found.",
    back: "Back to landing",
  },
  legal: {
    kicker: "Legal",
    last_updated: "Last updated \u00B7 {date}",
    placeholder_note: "This page is a placeholder for the MVP. The final, jurisdiction-specific text will be reviewed and replaced before public launch.",
    privacy: {
      title: "Privacy Policy",
      last_updated: "May 2026",
      intro: "Stockaily (operated by the Stockaily team) is committed to protecting your privacy. This policy explains what data we collect, why we collect it, and how you can exercise your rights.",
      s1_h: "What we collect",
      s1_b1: "Account data: your email, name, hashed password, and (if you sign in via Google) your Google account identifier.",
      s1_b2: "Usage data: which tickers you analyze, your watchlist, and aggregate counters used to power your usage quota and the homepage social-proof bar.",
      s1_b3: "Billing data: when you subscribe to Pro or Premium, our payment processor (CopeCart) handles the payment. We only store the resulting plan & subscription status.",
      s1_b4: "Broker survey: We ask signed-in members once which broker or brokers they use. We store the selected brokers, an optional free-text \u201COther\u201D value of up to 80 characters, or an explicit \u201CI don\u2019t use a broker yet\u201D response with the member\u2019s Stockaily account. Selecting a broker does not connect Stockaily to that broker. We use the information to improve onboarding and to prioritise possible future integrations. Reporting in the Owner Console is aggregated; individual accounts are not displayed. The response is deleted when the associated Stockaily account is deleted.",
      s2_h: "How we use the data",
      s2_b: "We use your data only to operate Stockaily \u2014 authenticating you, running your analyses, enforcing quotas and sending essential transactional emails. We do not sell or rent your data to third parties.",
      s3_h: "Cookies & local storage",
      s3_b: "We store a session token and a few non-tracking preferences (e.g. language, watchlist state) in your browser. See the Cookie Policy for details.",
      s4_h: "Your rights",
      s4_b: "You may at any time access, correct, export or delete your personal data by emailing privacy@stockaily.ai. We aim to respond within 30 days.",
      s5_h: "Contact",
      s5_b: "Questions about this policy? Reach out at privacy@stockaily.ai.",
    },
    terms: {
      title: "Terms of Service",
      last_updated: "May 2026",
      intro: "By using Stockaily you agree to these terms. They are designed to be readable \u2014 please reach out if anything is unclear.",
      s1_h: "Educational use only",
      s1_b: "Stockaily provides AI-assisted educational stock analysis. Nothing on the platform is investment advice, a solicitation to buy or sell securities, or a guarantee of future performance. You are solely responsible for your investment decisions.",
      s2_h: "Accounts",
      s2_b: "You're responsible for keeping your account credentials secure and for activity under your account. Stockaily may suspend accounts that violate these terms or applicable law.",
      s3_h: "Subscription & billing",
      s3_b: "Pro and Premium are billed monthly or yearly via CopeCart. You can cancel at any time from your billing dashboard; access continues until the end of the current period.",
      s4_h: "Acceptable use",
      s4_b: "Don't reverse-engineer, scrape, or attempt to circumvent rate limits or feature gates. Don't use Stockaily to publish automated investment recommendations to third parties without our written consent.",
      s5_h: "Liability",
      s5_b: "Stockaily is provided on an \"as-is\" basis. To the maximum extent permitted by law, we disclaim liability for indirect, incidental or consequential damages arising from your use of the platform.",
      s6_h: "Changes",
      s6_b: "We may update these terms; material changes will be communicated by email. Continued use of Stockaily after the effective date constitutes acceptance.",
    },
    cookies: {
      title: "Cookie Policy",
      last_updated: "May 2026",
      intro: "Stockaily uses a minimal set of cookies and browser storage entries \u2014 strictly to keep you signed in, remember your preferences, and run the platform.",
      s1_h: "Strictly necessary",
      s1_b1: "stockaily_token \u2014 keeps you signed in across sessions.",
      s1_b2: "session_token \u2014 server-issued cookie pairing your browser with your account.",
      s2_h: "Preferences",
      s2_b1: "stockaily_locale \u2014 remembers your selected language (English / Deutsch).",
      s2_b2: "Layout preferences (e.g. last selected price-chart timeframe) are stored locally to keep your workflow seamless.",
      s3_h: "Analytics & tracking",
      s3_b: "We do not embed third-party advertising trackers. Aggregate, anonymized counters (e.g. how many analyses ran today) are computed server-side and never tied to your identity outside our own systems.",
      s4_h: "Managing cookies",
      s4_b: "You can review or change your consent preferences at any time via {cookie_settings}. You can also clear cookies and local storage from your browser settings; note that clearing the session cookie will sign you out.",
    },
    impressum: {
      title: "Impressum",
      last_updated: "May 2026",
      intro: "Information pursuant to \u00A7 5 DDG (German Digital Services Act \u2014 Digitale-Dienste-Gesetz).",
      s1_h: "Operator",
      s1_b1: "Stockaily \u2014 AI-powered stock analysis platform",
      s1_b2: "Domain: stockaily.ai",
      s1_b3: "Contact: support@stockaily.ai",
      s1_b4: "Phone: +49 40 33483270",
      s2_h: "Responsible for content",
      s2_b: "The Stockaily team. The contact address above also serves for press inquiries and content-related questions.",
      s3_h: "Online dispute resolution",
      s3_b: "The European Commission provides a platform for online dispute resolution (ODR) at https://ec.europa.eu/consumers/odr. We are not obliged or willing to participate in dispute resolution proceedings before a consumer arbitration board.",
      s4_h: "Disclaimer",
      s4_b: "Despite careful content review, we assume no liability for the content of external links. The operators of the linked pages are solely responsible for their content.",
      s5_h: "Final business address \u2014 to be updated before launch",
      s5_b: "The full statutory summonable business address (and any applicable commercial register number and VAT ID) will be added to this page before public launch in line with the German \u00A7 5 DDG requirements. Direct telephone contact is already available at the number listed above.",
    },
  },
  dev: {
    label: "Dev only",
    test_plan: "test plan",
    test_plan_full: "Dev only \u00B7 test plan",
    note: "Bypass for QA \u00B7 remove this component before production launch.",
    active: "Active",
    plan_free_desc: "10 wl \u00B7 limited AI",
    plan_pro_desc: "Unlimited \u00B7 full AI",
    plan_premium_desc: "Pro + portfolio",
    switched_title: "Plan switched to {plan}",
    switched_desc: "Reloading the current page so access rules apply everywhere.",
    switched_failed: "Failed to switch plan",
  },

  analyze: {
    btn_watchlist: "Watchlist",
    btn_remove: "Remove",
    btn_reanalyze: "Re-analyze",
    chart_price_vs_fair_value: "Price vs Fair Value",
    growth_revenue: "Revenue",
    growth_earnings: "Earnings",
    // Phase 8.LP.POLISH — Company Profile accordion labels.
    // The FMP description body itself is NEVER translated —
    // only the surrounding UI labels are localized.
    profile: {
      accordion_title: "About this company",
      overview_label: "Company overview",
      show_more: "Show more",
      show_less: "Show less",
      row_sector: "Sector",
      row_industry: "Industry",
      row_exchange: "Exchange",
      row_country: "Country",
      row_currency: "Currency",
      row_ceo: "CEO",
      row_employees: "Employees",
      row_market_cap: "Market Cap",
      row_website: "Website",
    },
    // Hero / decision strip chip labels
    chip_upside: "Upside",
    chip_risk: "Risk",
    chip_horizon: "Horizon",
    // InstitutionalSnapshot tile labels
    inst: {
      classification: "Classification",
      quality_gate: "Quality gate",
      confidence: "Confidence",
      methods_used: "Methods used",
      risk_regime: "Risk regime",
      fair_value_confidence: "Fair value confidence",
      conviction: "{n}% conviction",
      blended: "{n} blended",
      how_we_got_there: "How we got there",
      detected_risks: "Detected risks",
      summary: "Institutional snapshot",
    },
    // AI Insights panel labels
    ai: {
      summary: "Summary",
      bull: "Bull",
      bear: "Bear",
      risks: "Risks",
      opportunity: "Opportunity",
      why_this_score: "Why this score",
      bull_case: "Bull case",
      bear_case: "Bear case",
      opportunity_reasoning: "Opportunity reasoning",
      key_drivers: "Key drivers behind the score",
    },
    // Status word translations
    status: {
      pass: "Pass",
      watch: "Watch",
      fail: "Fail",
      high: "High",
      medium: "Medium",
      low: "Low",
    },
    classification: {
      "SaaS / Software": "SaaS / Software",
      "Bank / Financial": "Bank / Financial",
      "REIT / Asset-heavy": "REIT / Asset-heavy",
      "Energy": "Energy",
      "Biotech": "Biotech",
      "Consumer Staples": "Consumer Staples",
      "Cyclical / Industrial": "Cyclical / Industrial",
      "Speculative": "Speculative",
      "Growth": "Growth",
      "Value": "Value",
    },
    horizon: {
      "Short-term": "Short-term",
      "Mid-term": "Mid-term",
      "Long-term": "Long-term",
    },
    kpi: {
      market_cap: "Market cap",
      pe: "P/E",
      forward_pe: "Forward P/E",
      eps: "EPS",
      revenue: "Revenue",
      revenue_growth: "Revenue growth",
      earnings_growth: "Earnings growth",
      profit_margin: "Profit margin",
      dividend_yield: "Dividend yield",
      roic: "ROIC",
      fcf_quality: "FCF quality",
      net_margin: "Net margin",
      debt_equity: "Debt / Equity",
      current_ratio: "Current ratio",
      gross_margin: "Gross margin",
      operating_margin: "Operating margin",
      sector_percentile: "Sector percentile",
    },
    locked_pro_cap_efficiency: "Pro \u00B7 capital efficiency",
    locked_pro_balance_sheet: "Pro \u00B7 balance-sheet quality",
    locked_pro_sector_cohort: "vs sector cohort",
    // Hero / ScorePanel
    hero: {
      ai_opportunity_score: "AI Opportunity Score",
      out_of_100: "/100",
      signal_label: "Signal",
      opportunity_level_label: "Opportunity Level",
      valuation_label: "Valuation",
      risk_level_label: "Risk Level",
      current_price: "Current price",
      fair_value: "Fair value",
      delta_vs_price: "{sign}${amount} vs price",
      delta_unavailable: "\u0394 vs price unavailable",
      range: "range ${low} \u2013 ${high}",
      valuation_unavailable: "Valuation could not be reconciled or verified",
      subscore_valuation: "Valuation",
      subscore_quality: "Business Quality",
      subscore_growth: "Growth",
      subscore_safety: "Risk & Safety",
    },
    // Valuation status display labels (so headline text reads in German)
    headline: {
      undervalued: "Undervalued by {pct}%",
      fairly_valued: "Fairly valued",
      overvalued: "Overvalued by {pct}%",
      unavailable: "Valuation unavailable",
    },
    signal: {
      opportunity: "Opportunity",
      neutral: "Neutral",
      risk: "Risk",
      unavailable: "Unavailable",
    },
    // Pre-launch terminology correction (2026-02-09) — the AI
    // Opportunity Score tier, presented as a distinct signal from
    // the genuine financial Risk Level.  Thresholds mirror
    // backend/poc/valuation_guards.py:  <45 = Low, 45-74 = Medium,
    // >=75 = High.  These labels intentionally never use "Risk"
    // wording so a low score no longer reads as a risk warning.
    opportunity_level: {
      low: "Low",
      medium: "Medium",
      high: "High",
      unavailable: "Unavailable",
    },
    valuation_status: {
      Undervalued: "Undervalued",
      Fair: "Fair",
      Overvalued: "Overvalued",
      Unavailable: "Unavailable",
      unavailable: "Unavailable",
    },
    classification: {
      top_opportunity: "Top Opportunity",
      opportunity: "Opportunity",
      fair: "Fair",
      neutral: "Fair",
      risk: "Risk",
      unavailable: "Unavailable",
    },
    // Valuation-safety unavailable banner (proven unit mismatch OR
    // unverified extreme outlier). Distinct from data_unavailable.
    valuation_unavailable: {
      title_unreconciled: "Valuation unavailable for {ticker}",
      title_outlier: "Valuation could not be verified for {ticker}",
      message_unreconciled_fallback:
        "The reporting-currency and share-unit inputs for this ticker could not be reconciled with the market quote. Stockaily only surfaces valuations it can defend, so no fair value, upside, or Opportunity Score is shown for this analysis.",
      message_outlier_fallback:
        "The computed fair value is an extreme outlier vs the current market price and could not be independently verified. Stockaily withholds unverified extreme results rather than surface a value that may be misleading.",
    },
    // Institutional Snapshot static strings
    snapshot: {
      title: "Institutional Snapshot",
      phase_badge: "Phase 2",
      fundamental_composite: "Fundamental Composite",
      composite_tip_title: "Fundamental Composite",
      composite_tip_intro: "Shows how strong the company looks based only on its fundamentals. It combines valuation, business quality, growth, and risk/safety into one diagnostic score. This is <b>not</b> the same as the AI Opportunity Score.",
      composite_relation_title: "How it differs from the AI Opportunity Score:",
      composite_relation_ai: "<b>AI Opportunity Score</b> = the final stock signal for the user. It also considers valuation upside, risk, confidence, sector context, and guardrails.",
      composite_relation_fc: "<b>Fundamental Composite</b> = the fundamentals layer behind the score. It helps explain why the AI Opportunity Score landed where it did.",
      weights_label: "Weights:",
      weight_valuation: "Valuation 35%",
      weight_quality: "Business Quality 30%",
      weight_growth: "Growth 20%",
      weight_risk: "Risk & Safety 15%",
      breakdown_valuation: "Valuation",
      breakdown_quality: "Business Quality",
      breakdown_growth: "Growth",
      breakdown_risk: "Risk & Safety",
      breakdown_trend: "Trend modifier",
      subtitle: "Deterministic, rule-based analysis adapted to this company's type.",
      tap_to_learn: "Tap any tile to learn what it means.",
      veto_hard_label: "Hard veto \u2014 score capped at 35",
      veto_soft_label: "Soft veto \u2014 score capped at 55",
      veto_hard_badge: "Forced Risk",
      veto_soft_badge: "Opportunity blocked",
      value_trap_label: "Value-trap risk",
      detected_risks: "Top detected risks",
      methods_used_label: "Methods used",
      method_fair_value: "Fair value",
      method_weight: "Weight",
      method_confidence: "Confidence",
      method_proxied: "Uses proxied inputs:",
      risk_category: {
        valuation: "Valuation",
        balance_sheet: "Balance sheet",
        operational: "Operational",
        capital_structure: "Capital structure",
        external: "External",
        other: "Other",
      },
      tile_tips: {
        classification_title: "Company classification",
        classification_body: "We classify the company using its sector, industry and financial profile. Different types (SaaS, Bank, REIT, Cyclical, etc.) get different valuation methods because what makes a bank cheap is very different from what makes a SaaS business cheap.",
        gate_title: "Quality gate",
        gate_body: "Pass / Watch / Fail status based on hard fundamentals: persistent losses, negative cash flow, negative equity, weak ROIC. A Fail caps the score regardless of valuation \u2014 even a 'cheap' bad business stays risky.",
        confidence_title: "Analysis confidence",
        confidence_body: "Combines how complete the input data is, how consistent our valuation methods are, and a small penalty for any estimated/proxied metrics or sector medians used as fallback. Range 25%\u201395%. High >70% / Medium 45\u201370% / Low <45%.",
        methods_title: "Multi-method valuation",
        methods_body: "Instead of a single DCF, we run several valuation methods chosen to fit the company type \u2014 for example EV/ARR for SaaS, Price/Book for banks, FFO for REITs \u2014 then weight them and average. This avoids one bad assumption distorting the result.",
        risk_regime_title: "Risk regime",
        risk_regime_body: "Low / Medium / High based on volatility (beta), leverage, profitability and the classification itself. High-risk companies are capped at moderate scores \u2014 they cannot read as a clear opportunity even when the math says they're cheap.",
        fv_confidence_title: "Fair Value Confidence",
        fv_confidence_body: "Shows how reliable the fair value estimate is. <b>High</b> confidence means the valuation signals are consistent and the estimated range is relatively narrow. <b>Medium</b> confidence means the estimate is usable, but some signals are mixed. <b>Low</b> confidence means the valuation range is wide or the inputs are less consistent. Low confidence does not mean the stock is bad — it means the fair value estimate is more uncertain.",
      },
    },
    // Phase 8.LP.TIPS — hover tooltips for the four Fundamental
    // sub-score bars on the analysis hero. Kept separate from the
    // ``snapshot.tile_tips`` group so both label styles can evolve
    // independently.
    subscore_tips: {
      valuation_title: "Valuation",
      valuation_body: "Measures how attractive the stock looks compared with its estimated fair value and valuation multiples. Higher = cheaper or more attractive valuation. Lower = expensive or less attractive valuation.",
      quality_title: "Business Quality",
      quality_body: "Measures the strength of the business model. It looks at profitability, margins, cash-flow quality, balance sheet strength, and return on capital. Higher = stronger business quality.",
      growth_title: "Growth",
      growth_body: "Measures how strongly the company is growing. It considers revenue growth, earnings growth, and other growth signals when available. Higher = stronger growth profile.",
      safety_title: "Risk & Safety",
      safety_body: "Measures financial risk and business stability. It looks at balance sheet strength, debt levels, volatility, and risk signals. Higher = safer profile. Lower = higher risk.",
      ai_score_title: "AI Opportunity Score",
      ai_score_body: "The AI Opportunity Score is Stockaily's final stock signal. It combines valuation upside, business quality, growth, risk, confidence, and sector context. Higher scores indicate a more attractive opportunity based on the model.",
    },
    // Key Drivers panel
    drivers: {
      title: "Key drivers behind the score",
      subtitle: "Based on cash flow, valuation, profitability, growth and balance-sheet analysis.",
      tap_hint: "Tap any metric to learn what it means.",
      label: {
        fcf_yield: "Free cash flow yield",
        cash_flow_quality: "Cash flow quality",
        roic: "Return on invested capital",
        revenue_growth: "Revenue growth",
        pe_vs_sector: "P/E versus sector",
        debt_to_equity: "Debt-to-equity",
        fair_value_confidence: "Fair value confidence",
      },
      tip: {
        fcf_yield_title: "Free cash flow yield",
        fcf_yield_body: "How much real cash the business produces relative to what investors pay for it. Above 4% is attractive \u2014 it means the business is cheap relative to the cash it generates.",
        cash_flow_quality_title: "FCF Quality",
        cash_flow_quality_body: "<b>What it is</b><br/>Shows how well reported earnings turn into real free cash flow.<br/><br/><b>Why it matters</b><br/>Strong cash-flow quality means profits are backed by actual cash generation, not just accounting earnings.<br/><br/><b>Rule of thumb</b><br/>80–100 = strong<br/>60–79 = solid<br/>40–59 = mixed<br/>Below 40 = weak or higher risk",
        roic_title: "Return on invested capital (ROIC)",
        roic_body: "How much profit the business earns for every dollar of capital tied up in it. Great compounders sit above 15%. Below 8% means the company struggles to create value from its assets.",
        revenue_growth_title: "Revenue growth momentum",
        revenue_growth_body: "How fast sales are growing year-over-year. Above 15% is strong, fast-growth territory. 5\u201315% is steady. Flat or negative often signals competitive or demand pressure.",
        pe_vs_sector_title: "P/E versus sector",
        pe_vs_sector_body: "Price-to-earnings ratio compared to peers in the same sector. Below 1.0\u00D7 means the stock trades cheaper than its industry. Above 1.2\u00D7 means investors are paying a premium that future growth has to justify.",
        debt_to_equity_title: "Leverage profile",
        debt_to_equity_body: "Total debt divided by shareholder equity. Under 1\u00D7 is conservative and resilient. Above 2\u00D7 is high leverage that magnifies losses during downturns or rising-rate environments.",
        fair_value_confidence_title: "Fair Value Confidence",
        fair_value_confidence_body: "Shows how reliable the fair value estimate is. <b>High</b> confidence means the valuation signals are consistent and the estimated range is relatively narrow. <b>Medium</b> confidence means the estimate is usable, but some signals are mixed. <b>Low</b> confidence means the valuation range is wide or the inputs are less consistent. Low confidence does not mean the stock is bad — it means the fair value estimate is more uncertain.",
      },
    },
    // Price chart
    chart: {
      title: "Price vs Fair Value",
      subtitle: "Forward-looking projection \u00B7 cheap zone (green) below the curve, expensive zone (red) above.",
      info_aria: "About this fair value curve",
      info_body: "This curve represents an estimated fair value based on current fundamentals and projected growth \u2014 not a historical recalculation. Fair value evolves over time; this is a forward-looking estimate.",
      overlay_toggle: "Show Fair Value Overlay",
      legend_cheap: "Cheap zone",
      legend_expensive: "Expensive zone",
      legend_fair_value: "Fair value (projected)",
      legend_fair_value_short: "Fair value",
      legend_current_price: "Current price",
      legend_footnote: "Fair value evolves over time \u2014 this is a forward-looking estimate.",
      last_5_years: "Last 5 years",
      tooltip_price: "Price",
      tooltip_fair_value: "Fair value",
      verdict_undervalued: "Undervalued by +{pct}%",
      verdict_fair: "Fairly valued",
      verdict_overvalued: "Overvalued by {pct}%",
      now_pill: "NOW",
      fair_value_pill: "FAIR VALUE",
      fallback_plan_restricted: "Historical chart data is unavailable on the current data plan",
      fallback_rate_limited: "Historical chart data is temporarily rate-limited",
      fallback_empty: "No historical price points were returned for this symbol",
      fallback_unavailable: "Historical chart data is temporarily unavailable",
      fallback_secondary: "The valuation summary and all other analysis sections remain accurate; only the historical price chart is missing.",
      overlay_label_mobile: "Fair value overlay",
      microexplanation_desktop: "The green curve is an <strong>estimated fair value projection</strong> based on current fundamentals (cash flow, valuation, growth) extrapolated with a steady annual growth assumption \u2014 it is not a historical recalculation of fair value. When the price sits below the curve the stock is in the cheap zone; above it is the expensive zone.",
      microexplanation_mobile: "The green curve is a <strong>forward-looking fair value projection</strong> \u2014 below it is the cheap zone, above it the expensive zone.",
    },
    // AI Insights additional labels
    ai_extra: {
      panel_title: "AI Insights",
      free_preview_badge: "FREE PREVIEW",
      generating_badge: "GENERATING",
      generating_label: "Generating AI insights\u2026",
      generating_timed_out: "AI insights are still being generated. They will appear automatically when ready.",
      generating_footnote: "Valuation, score, and KPIs above are already finalized. The full AI narrative is being prepared in the background.",
      free_upgrade_blurb: "Upgrade for institutional-grade AI research, bull/bear cases and risk analysis.",
      upgrade_btn: "Upgrade",
      locked_pro_tab_hint: "Available on Pro. Upgrade to unlock this section.",
      locked_teaser_eyebrow: "Pro insights preview",
      locked_teaser_body:
        "Upgrade to unlock the full Bull Case, Bear Case, Key Risks, Confidence reasoning and score explanation \u2014 rendered as institutional-grade AI narratives for this stock.",
      no_risks: "No material risks identified.",
      no_items: "No items identified.",
      drivers_positive: "What's working",
      drivers_negative: "What's pressuring the score",
      section_top_risks: "Top institutional risks",
      section_confidence: "Why our confidence is at this level",
      section_drivers: "Why this stock scores this way",
      ai_paused_banner: "AI commentary is briefly paused. Valuation and KPIs remain accurate.",
      disclaimer_not_advice: "This is not financial advice.",
    },
    // Phase 8.LP.FLE — Free-quota-exhausted locked preview.
    // Copy is intentionally factual, not urgency-based; the {used}
    // and {limit} placeholders keep the "X of X" line context-aware
    // between the first-30d (10/10) and rolling (5/5) phases.
    locked_preview: {
      headline: "Free analysis limit reached",
      subheadline: "You\u2019ve used all {used} of {limit} free analyses in this period.",
      reset_with_date: "Your next free analyses unlock on {date}.",
      reset_generic: "Your free analysis allowance resets with your rolling 30-day window.",
      upgrade_line: "Upgrade to Pro for unlimited analyses and full access instantly.",
      cta_primary: "Upgrade to Pro",
      cta_secondary: "See plans",
      disclaimer: "Educational valuation tooling. Not financial advice.",
      locked_label: "Locked",
      sections: {
        fair_value: {
          title: "Stockaily Fair Value",
          hint: "Our model-based estimate of intrinsic value.",
        },
        opportunity_score: {
          title: "Opportunity Score",
          hint: "Composite 0\u2013100 signal combining valuation, quality and risk.",
        },
        upside_downside: {
          title: "Upside / Downside",
          hint: "Difference between Fair Value and the current price.",
        },
        bull_case: {
          title: "Bull Case",
          hint: "The strongest arguments for owning this stock.",
        },
        bear_case: {
          title: "Bear Case",
          hint: "The strongest arguments against owning this stock.",
        },
        key_risks: {
          title: "Key Risks",
          hint: "Business, valuation and financial risks surfaced by the model.",
        },
        ai_narrative: {
          title: "Full AI Narrative",
          hint: "Institutional-style write-up explaining the valuation.",
        },
        valuation_breakdown: {
          title: "Valuation Breakdown",
          hint: "Multi-method valuation with per-method sensitivity.",
        },
      },
    },
    // KPI tooltip body labels (used in KpiCard.js)
    kpi_tip: {
      what_label: "What it is \u2014 ",
      why_label: "Why it matters \u2014 ",
      rule_label: "Rule of thumb \u2014 ",
    },
    // Status decorations
    risk_chip: {
      forced: "Forced Risk",
      blocked: "Opportunity blocked",
    },
    // Page-level messages
    session: {
      waiting_title: "Waiting for session",
      waiting_body: "Verifying your account so we can run the {ticker} analysis. This usually takes under 2 seconds.",
    },
    unavailable: {
      title: "Data unavailable for {ticker}",
      message_fallback: "We couldn't retrieve live market data for this ticker right now. This is usually a temporary upstream issue \u2014 please try again in a few minutes.",
      try_again: "Try again",
      pick_different: "Pick a different ticker",
    },
    limit: {
      title: "You've hit your free analysis quota.",
      subtitle: "Upgrade to Pro for unlimited analyses and full AI insights.",
      upgrade_cta: "Upgrade",
    },
    sparse: {
      title: "Limited fundamental data for {ticker}",
      body: "We could only access a partial set of financial endpoints for this stock \u2014 so the AI analysis and valuation below may be less reliable than usual. Missing fields are shown as \u2014 rather than zeros so signals stay honest. We'll keep retrying as our data coverage expands.",
    },
    auth: {
      log_in_cta: "Log in",
      create_account_cta: "Create account",
      gate_title: "Sign in to analyze {ticker}",
      gate_subtitle: "Stockaily tracks your free quota in your account. Sign in or create one to run AI valuations.",
      session_expired_toast: "Session expired \u2014 please sign in again.",
    },
    // Slice 1 (2026-02-14) — anonymous stock-analysis gate.
    // A logged-out visitor who selects a ticker in the public search
    // and lands on /analyze/{ticker} sees this marketing gate instead
    // of the previous technical "Session expired" surface.  Every
    // string uses semantic slots so bilingual parity is ensured.
    anonymous_gate: {
      title: "Create your free account to analyze {ticker}",
      body: "Get its estimated Fair Value, Opportunity Score, upside/downside and risk analysis in under 30 seconds.",
      primary_cta: "Create free account",
      secondary_cta: "Log in",
      microcopy: "10 free analyses in your first month. No credit card required.",
    },
    not_found: {
      head_unsupported: "Unsupported asset",
      head_timeout: "Analysis took too long",
      head_session_expired: "Session expired",
      head_error: "Something went wrong",
      head_default: "We couldn\u2019t find this stock",
      body_unsupported: "Stockaily currently supports stock analysis only. \u201C{ticker}\u201D looks like a cryptocurrency, FX pair, or futures contract \u2014 none of which fit our equity-focused valuation engine (DCF, ROIC, P/E, balance sheet quality).",
      body_timeout: "The analysis service is taking longer than expected. This usually clears in a few seconds. Try again, or pick a different ticker below.",
      body_session_expired: "Your session has expired. Please sign in again to analyze {ticker}.",
      body_error: "Our analysis engine hit an unexpected error. Try again in a moment, or pick a different ticker below.",
      body_default: "We couldn\u2019t find \u201C{ticker}\u201D in our database. Check the spelling, or try the ticker symbol instead of the company name.",
      this_stock: "this stock",
      sign_in_continue: "Sign in to continue",
      back_after_signin: "You\u2019ll be sent back to {ticker} after signing in.",
      back_after_signin_default: "the analyze page",
      search_placeholder_unsupported: "Try a stock ticker or company name\u2026",
      search_placeholder_default: "Try another ticker or company name\u2026",
      tip_unsupported_html: "Stockaily analyzes <span class=\"text-foreground/90\">public equities</span> \u2014 stocks, ADRs, and ETFs.",
      tip_default_html: "Tip: stock <span class=\"text-foreground/90\">ticker symbols</span> (like AAPL, MSFT) usually work better than company names.",
      suggestions_unsupported: "Try these stocks instead",
      suggestions_default: "Popular tickers",
      go_back: "Go back",
    },
  },

  // Phase 7.UI.10 — AI Alpha Signals header + Pro/Full Access pills.
  signals: {
    title: "AI Alpha Signals",
    subtitle: "Institutional-grade pattern detection on {ticker} \u2014 risk flags, edge signals & sector context.",
    full_access: "Full access",
    cta_title: "Unlock the rest of the AI's reasoning",
    cta_subtitle: "Full risk decomposition, scenario modeling and sector percentile \u2014 no caps.",
    cta_see_plans: "See plans",
    cta_upgrade: "Upgrade to Pro",
    items: {
      value_trap: {
        title: "Potential value trap risk detected",
        preview: "Stock screens {upside_abs}% cheap, but our model flags",
        locked: "deteriorating cash conversion (FCF/NI {fcf_to_ni}), thinning margins and balance-sheet fragility \u2014 the AI reads this as structural rather than cyclical undervaluation.",
      },
      earnings_quality: {
        title: "Earnings quality warning",
        preview: "Reported earnings are running ahead of cash flow by",
        locked: "~{gap_pct}%, a pattern historically associated with accruals-driven beats and earnings normalization within 4\u20136 quarters.",
      },
      multiple_compression: {
        title: "Sector multiple compression risk",
        preview: "Trades at a premium to sector P/E; mean-reversion implies",
        locked: "a {comp_pct}% multiple compression scenario over 18\u201324 months would offset most of the upside from forward earnings growth.",
      },
      balance_sheet: {
        title: "AI detected hidden balance-sheet risk",
        preview: "D/E at {de}x and net debt at {nd_to_mc_pct} of mkt cap suggest",
        locked: "covenant tightness in a higher-rate environment. Stress test under a 200bp upward shock removes ~14\u201322% of equity value in our model.",
      },
      low_confidence: {
        title: "Low valuation confidence",
        preview: "Valuation range is unusually wide due to",
        locked: "speculative cash-flow profile and limited visibility on terminal economics. AI suggests sizing positions ~40% below normal conviction.",
      },
      quality_value_alignment: {
        title: "High-conviction quality + value alignment",
        preview: "Both quality and valuation sub-scores sit in the top quartile \u2014",
        locked: "a configuration that has historically outperformed the sector by ~6\u20139% annually over 3-year holding periods in our backtest universe.",
      },
      growth_durability: {
        title: "Durable growth profile detected",
        preview: "Revenue, earnings and FCF growth all trend in the top decile,",
        locked: "with internal compounding signals that map to a 3-year intrinsic value step-up of roughly 1.4\u20131.7\u00D7 current fair value.",
      },
      neutral_baseline: {
        title: "Valuation tightly clustered around fair value",
        preview: "Modeled fair value sits within the noise band of price \u2014",
        locked: "AI sees no asymmetric setup. Pro members see the sector percentile ranking, historical signal accuracy and full sub-score commentary.",
      },
      sector_percentile: {
        title: "Sector percentile ranking",
        preview: "This name's AI Opportunity Score sits in the",
        locked: "top {top_pct}% of {sector} stocks tracked by Stockaily \u2014 Pro shows live percentile vs 5 closest peers and historical accuracy of this signal.",
      },
    },
  },
  theme: {
    label: "Theme",
    dark: "Dark",
    light: "Light",
    switch_to_dark: "Switch to dark mode",
    switch_to_light: "Switch to light mode",
  },
  portfolio: {
    page: {
      title: "Portfolio Intelligence",
      subtitle: "Track your holdings against Stockaily's fair value, opportunity score, and risk framework.",
      disclaimer: "Stockaily provides educational valuation tooling and does not give investment advice.",
    },
    common: {
      loading: "Loading your portfolio…",
      pending: "Analysis pending",
    },
    review: {
      title: "AI Portfolio Review",
      summary: "Summary",
      strengths: "Strengths",
      weaknesses: "Weaknesses",
      risks: "Risks",
      concentration: "Concentration Risks",
      top_opportunity: "Top Opportunity",
      largest_risk: "Largest Valuation Risk",
      research_priorities: "Research Priorities",
      confidence: "Confidence",
      updated_at: "Updated",
      cta_analyze: "Analyze Portfolio",
      cta_update: "Update Portfolio Analysis",
      cta_running: "Generating analysis…",
      badge_premium_ai: "Premium AI",
      disclaimer: "Educational analysis only. Not investment advice.",
    },
    tooltip: {
      portfolio_value: "Total current market value of all holdings, using the latest available prices.",
      estimated_fair_value: "Sum of shares × Stockaily fair value across all holdings.",
      portfolio_upside: "(Estimated fair value − portfolio value) / portfolio value. Positive means the book trades below estimated fair value.",
      unrealized_pl: "Unrealized profit or loss versus your weighted-average cost basis. Does not include sold positions.",
      weighted_opportunity_score: "Position-weighted Stockaily Opportunity Score on a 0–100 scale. Higher is more attractive.",
      risk_level: "Risk and valuation are separate dimensions. A stock can appear undervalued and still carry High Risk due to debt, volatility, low fair-value confidence or business uncertainty.",
      position_weight: "This holding's share of total portfolio market value.",
      fair_value: "Stockaily estimate of the stock's fair value based on the deterministic valuation model.",
      ai_score: "Stockaily Opportunity Score for this holding on a 0–100 scale.",
      concentration: "Largest single-holding weight in the portfolio. High concentration increases idiosyncratic risk.",
      top_opportunity: "Holding with the strongest combination of upside and score, surfaced for further review.",
      largest_valuation_risk: "Holding that appears most overvalued relative to estimated fair value.",
      shares: "Number of shares you hold. Fractional shares up to 6 decimals are supported.",
      average_buy_price: "Share-weighted average cost per share across all your BUY transactions for this ticker.",
    },
    risk: { low: "Low", medium: "Medium", high: "High" },
    kpi: {
      value: "Portfolio Value",
      estimated_fair_value: "Estimated Fair Value",
      upside_downside: "Upside / Downside",
      upside_downside_hint: "Estimated vs current portfolio value",
      unrealized_pl: "Unrealized P/L",
      weighted_score: "Weighted Opportunity Score",
      weighted_score_hint: "Value-weighted across holdings",
      risk_level: "Risk Level",
      risk_hint: "Value-weighted across holdings",
      unrealized_pl_hint_pending: "Pending live prices",
      cost_basis: "Cost basis",
      vs_value: "vs current value",
      coverage_partial_badge: "Partial",
      coverage_partial_short_label: "{count}/{total} holdings",
      coverage_partial_notice: "Fair Value shown covers {count} of {total} holdings ({ratio} of portfolio market value) — not the whole portfolio.",
      coverage_partial_label: "Covers {count} of {total} holdings · {ratio} of portfolio market value",
      coverage_partial_tooltip: "Fair Value is available for only {count} of {total} holdings. The percentage shown is the share of portfolio MARKET VALUE those holdings represent, not the share of holdings by count.",
      coverage_vs_covered_value: "vs covered holdings",
    },
    chart: {
      title: "Portfolio Growth",
      subtitle: "Value vs estimated fair value",
      portfolio_value: "Portfolio Value",
      estimated_fair_value: "Estimated Fair Value",
      sp500_series: "S&P 500 Benchmark (Index)",
      sp500_disclaimer: "S&P 500 is a price index and does not include reinvested dividends.",
      // Phase 8.E.3 — new KPI + method + data-quality copy
      kpi_portfolio_return: "Portfolio Return",
      kpi_benchmark_return: "S&P 500 Return",
      kpi_relative_performance: "Relative Performance",
      kpi_relative_performance_hint: "Portfolio return minus S&P 500 return, expressed in percentage points.",
      kpi_unavailable: "Unavailable",
      method_twr_daily: "Time-weighted return",
      method_modified_dietz: "Modified Dietz fallback",
      method_none: "Insufficient history",
      method_tooltip_twr: "Time-weighted return isolates market performance by removing the effect of your buys and sells. Higher-quality method.",
      method_tooltip_dietz: "Modified Dietz is used as a fallback when daily portfolio snapshots are too sparse for time-weighted linking. Directional accuracy is preserved.",
      method_tooltip_scope: "This measures the tracked invested portfolio only. Idle broker cash is not included.",
      data_quality_good: "Good",
      data_quality_partial: "Partial data",
      data_quality_insufficient: "Insufficient",
      fair_value_toggle_show: "Show Fair Value",
      fair_value_toggle_hide: "Hide Fair Value",
      preview_locked_range: "Available in the full Portfolio",
      fair_value_axis_label: "Estimated Fair Value ($)",
      index_axis_label: "Performance index",
      // Phase 8.E.4 visual-review — currency-mode chart labels.
      value_axis_label: "Portfolio & Fair Value ($)",
      value_mode_subtitle: "Portfolio value history",
      value_mode_notice: "Daily performance history is currently incomplete, so the chart shows portfolio value. The Modified Dietz return above summarises the period return.",
      fv_coverage_gap_title: "Fair-Value line has coverage gaps",
      fv_coverage_gap_body: "The Fair-Value line connects only days whose covered holding set matches. {n} coverage change(s) were detected and are drawn as gaps so sums over different holding sets are never compared.",
      value_mode_benchmark_note: "S&P 500 comparison is shown as a KPI value above; the visual index comparison requires more complete daily history.",
      method_tooltip_dietz_value_chart: "The percentage return is calculated with Modified Dietz. The chart shows the underlying portfolio value because daily performance history is incomplete.",
      // Phase 8.E.4 visual-review v6 — benchmark role disclaimer.
      benchmark_disclaimer: "The S&P 500 is used as the portfolio benchmark. It is a price index and does not include reinvested dividends.",
      // Phase 8.E.4 visual-review v8 — plain-language explanation of
      // what the "Performance index (base = 100)" scale means.
      index_explanation: "The Performance index compares the Portfolio and the S&P 500 Benchmark. Both start at 100 at the beginning of the selected range — a value of 143 therefore means +43 % growth since the start of that range.",
      // Degraded states
      insufficient_history_title: "Your portfolio history is getting started",
      insufficient_history_body: "Stockaily records your portfolio each day. With complete dated purchases and sales and available historical prices, earlier portfolio values can also be reconstructed. Otherwise, the chart starts from usable recorded valuations.",
      reconstructed_title: "Portfolio history reconstructed from transactions",
      reconstructed_body: "Earlier daily values use your dated purchases and sales and historical closing prices. Returns adjust for purchases and sales. Historical Fair Value is shown only where Stockaily recorded an analysis; it is not recreated for earlier dates.",
      reconstruction_dates_hint: "For an earlier history, confirm missing purchase dates under the holding’s menu → View Transactions → Add purchase date. Only enter the actual date; leave it blank if unknown.",
      history_building_label: "History building",
      hide_notice: "Hide",
      show_notices: "Show notices ({n})",
      hide_history_notice: "Hide portfolio history information",
      hide_data_notices: "Hide portfolio data notices",
      history_building_title: "Your portfolio history is growing",
      history_progress: "Tracking since {date} · {n} daily valuations in this range.",
      history_progress_no_date: "{n} daily valuations in this range.",
      history_building_body: "Stockaily records your portfolio each day. Earlier values can also be reconstructed when all purchase and sale dates and historical prices are available. Returns account for dated purchases and sales; sale proceeds held as cash are not part of the invested portfolio.",
      history_benchmark_body: "The S&P 500 comparison line becomes available when this range has enough usable daily performance data and benchmark prices. Until then, the benchmark return is shown above when available.",
      missing_dates_title: "Add missing purchase dates",
      missing_dates_body: "An additional purchase is missing its date, so we cannot reliably calculate your return. Open the affected holding’s menu → View Transactions → Add purchase date, and confirm the actual purchase date.",
      transactions_title: "Review your transactions",
      transactions_body: "Some transactions cannot be placed reliably in your performance history. Check the dates in View Transactions. Waiting for more snapshots will not resolve these entries.",
      updating_title: "Updating portfolio values",
      updating_body: "The daily valuation for your changed holdings is not ready yet. The previous daily valuation is hidden until it is available.",
      // Phase 8.E.3 rev-2 — baseline copy
      baseline_note: "Performance tracking starts on {date}, the first usable portfolio valuation in this range.",
      baseline_setup_excluded_note: "Earlier setup-period snapshots are excluded from the return calculation.",
      unstable_baseline_title: "Performance tracking not yet available",
      unstable_baseline_body: "Performance tracking will begin once Stockaily has a complete and stable portfolio snapshot.",
      method_tooltip_baseline_scope: "Performance is measured from the first complete Stockaily portfolio snapshot. Modified Dietz is used because daily history is currently limited.",
      benchmark_unavailable_notice: "Benchmark data is temporarily unavailable.",
      fair_value_unavailable_notice: "Fair-value history is not yet available for this range.",
      partial_data_notice: "Some days lack complete data. Results shown are directional.",
      // Tooltip labels
      tt_date: "Date",
      tt_portfolio_index: "Portfolio Index",
      tt_portfolio_return_since_start: "Portfolio since {range} start",
      tt_portfolio_value: "Portfolio Value",
      tt_sp500_index: "S&P 500 Index",
      tt_sp500_return_since_start: "S&P 500 since {range} start",
      tt_sp500_price: "S&P 500 Price",
      tt_relative_performance: "Relative Performance",
      tt_fair_value: "Est. Fair Value",
      tt_fair_value_partial: "Partial coverage",
      fv_analysis_as_of: "Oldest included analysis: {date}",
      fv_analysis_date_unknown: "Analysis date unavailable",
      fv_carried_forward: "Carried forward",
      fv_carried_forward_notice: "Some fair-value estimates use earlier analyses. Hover over a point to see the analysis date; market prices are tracked separately.",
      tt_cash_flow_buy: "BUY",
      tt_cash_flow_sell: "SELL",
      // Legacy keys kept for backward compat with older test-ids
      sp500_coming_soon: "S&P 500 benchmark — coming soon",
      empty_title: "Portfolio history will appear here",
      empty_body: "Your portfolio history will appear here after the first snapshot.",
      one_snapshot_title: "Portfolio tracking has started",
      one_snapshot_body: "More history will appear over time.",
      fair_value_start_note: "Estimated Fair Value tracking started on {date}. Earlier recorded values are unavailable.",
      legend_portfolio_value_tip: "Portfolio Value is the current market value of all open positions.",
      legend_fair_value_tip: "Estimated Fair Value is the sum of shares multiplied by Stockaily's current fair-value estimate for each holding.",
      not_enough_history: "Portfolio Growth needs at least two valid daily snapshots. Your current holdings and valuation data are available, but performance history will appear after another valid snapshot.",
      history_days_note: "Only {days} day{plural} of recorded portfolio history are available.",
      interpretation_upside: "Potential valuation upside",
      interpretation_near_fv: "Near estimated fair value",
      interpretation_risk: "Valuation risk elevated",
      fv_interpretation_note: "Estimated Fair Value is a model-based estimate, not a sell signal. Use it to identify where valuation risk or potential opportunity is concentrated.",
    },
    freshness: {
      cached: "Cached",
      updated_at: "Updated: {date}",
      up_to_date: "Up to date",
      stale_title: "Portfolio changed since this analysis",
      stale_body: "Update the analysis to reflect the latest holdings.",
      // Phase 8.PH — 4-state AI review status labels.
      status_up_to_date: "Up to date",
      status_data_temporarily_stale: "Data temporarily stale",
      status_market_data_unavailable: "Market data unavailable",
      status_portfolio_changed_refresh_analysis: "Portfolio changed — refresh analysis",
      // Phase 8.B QA-7 — locale mismatch banner. We show this when the
      // cached review's locale differs from the current UI locale so the
      // user understands why the language doesn't match what they
      // selected. We do NOT auto-trigger a refresh — user must click
      // Update Portfolio Analysis themselves to regenerate.
      locale_mismatch_en: "This analysis was generated in English. Update the portfolio analysis to create a German version.",
      locale_mismatch_de: "This analysis was generated in German. Update the portfolio analysis to create an English version.",
    },
    badge: {
      // Phase 8.B QA-7 — Portfolio Intelligence badges. Mirror the
      // wording in both locales so the badge follows the active UI
      // language, not the language of the cached review.
      intelligence: "Portfolio Intelligence · AI",
      intelligence_ai_assisted: "Portfolio Intelligence · AI Assisted",
      intelligence_deterministic: "Portfolio Intelligence · Deterministic",
    },
    concentration_band: {
      low: "Low concentration",
      moderate: "Moderate concentration",
      high: "High concentration",
    },
    loading: {
      page: "Loading your portfolio…",
      holdings: "Loading your holdings…",
      prices: "Updating live prices…",
      valuation: "Refreshing valuation data for {count} holdings…",
      metrics: "Recalculating portfolio metrics…",
      ai_review: "Building your Portfolio AI Review…",
      cached_loaded: "Using the latest cached analysis.",
      stale_warning: "Portfolio changed since this analysis.",
      deterministic_active: "Using deterministic portfolio intelligence.",
      provider_unavailable: "AI provider is temporarily unavailable. Showing deterministic analysis.",
      cooldown_active: "Refresh available in {minutes}m.",
      daily_limit: "Daily AI refresh limit reached. Try again tomorrow.",
      partial_data: "One holding is still being analyzed.",
    },
    // Phase 8.B QA-7 — Rotating loader strings shown while the AI
    // review is being generated. Investor-neutral wording (no
    // celebrity names). Both locales must carry equivalent flavour.
    loader: {
      running_value_lens: "Running the value lens on your holdings…",
      buffett_lens: "Running the Buffett lens on your holdings…",
      scanning_value_traps: "Scanning for hidden value traps…",
      checking_carriers: "Checking who's carrying the portfolio…",
      stress_testing: "Stress-testing the weak spots…",
      price_vs_value: "Testing whether price and value still agree…",
      separating_signal: "Separating signal from market noise…",
    },
    completeness: {
      partial_warning: "Fair value coverage: {pct}% of portfolio value. {excluded} holding(s) not yet analyzed.",
      // 2026-02 Staging regression follow-up — accurate three-state copy:
      state_refresh_in_progress: "AI refresh in progress",
      state_analysis_missing: "Analysis pending",
      state_data_unavailable: "Fair Value unavailable",
      coverage_summary: "Fair Value coverage: {pct}% of portfolio value",
      clause_detail: "{holdings} affected",
      partial_detail: "{holdings} without Fair Value · covered: {pct}% of portfolio value",
      holdings_singular: "1 holding",
      holdings_plural: "{count} holdings",
      missing_data: "Data unavailable",
    },
    diagnostics: {
      title: "Portfolio Diagnostics",
      subtitle: "Admin-only view of portfolio health for one user.",
      user_id_label: "User ID",
      load_btn: "Load diagnostics",
      action_clear_cache: "Clear AI review cache",
      action_rebuild_snapshot: "Rebuild today's snapshot",
      action_recalc_metrics: "Rerun deterministic metrics",
      confirm_clear_cache: "Clear cached AI review for this user?",
      confirm_rebuild_snapshot: "Rebuild today's snapshot for this user?",
      confirm_recalc_metrics: "Rerun deterministic metrics for this user?",
      action_ok: "Done.",
      action_failed: "Action failed.",
      not_found: "No portfolio data for this user.",
    },
    refresh: {
      prices: "Refresh Prices",
      analysis: "Refresh Portfolio Analysis",
      next_in_minutes: "Available in {n}m",
      refreshes_left: "Refreshes left today",
      last_analysis: "Last analysis",
      tooltip_no_holdings: "Add a holding first.",
      // Phase 8.B QA-9 — daily-limit-aware availability copy.
      // Precedence: when refreshes_left_today === 0 we hide the cooldown
      // countdown and tell the user when the daily allowance resets
      // (preferring the exact reset time when the backend exposes it).
      available_tomorrow: "Available again tomorrow",
      available_tomorrow_at: "Available again tomorrow at {time}",
      tooltip_daily_limit_reached: "Daily Portfolio AI refresh limit reached.",
    },
    kpi_extra: {
      unrealized_pl_hint_pending: "Pending live prices",
    },
    help: {
      title: "How portfolio scoring works",
      subtitle: "Stockaily turns the same valuation logic behind each stock analysis into a portfolio view.",
      row_value_k: "Portfolio Value",
      row_value_v: "Sum of shares × current price across your holdings. Shown only when current prices are available.",
      row_fv_k: "Estimated Fair Value",
      row_fv_v: "Sum of shares × Stockaily fair value per share. Based on the same valuation engine used for individual stock analyses.",
      row_upside_k: "Upside / Downside",
      row_upside_v: "Shows how far estimated fair value sits above or below your current portfolio value.",
      row_score_k: "Weighted Opportunity Score",
      row_score_v: "Each holding's AI Opportunity Score, weighted by its share of your portfolio. Higher means more opportunity versus fair value.",
      row_risk_k: "Risk Level",
      row_risk_v: "Each holding's risk level, weighted across the portfolio.",
      disclaimer: "Stockaily is an educational valuation tool. Not investment advice.",
      close: "Got it",
    },
    actions: {
      add_holding: "Add Holding",
      retry: "Try again",
    },
    export: {
      menu_label: "Export",
      aria_label: "Export portfolio",
      title_hint: "Export PDF / CSV",
      pdf_label: "Export PDF report",
      pdf_hint: "Branded Portfolio Intelligence Report",
      csv_label: "Export CSV (holdings)",
      csv_hint: "Machine-readable holdings snapshot",
      pdf_success: "PDF report ready.",
      csv_success: "CSV export ready.",
      failed: "Export failed. Please try again.",
      premium_required: "Portfolio export is a Premium feature.",
    },
    insights: {
      top_opportunity: "Top Opportunity",
      top_opportunity_hint: "Position with the largest upside vs estimated fair value.",
      largest_risk: "Largest Valuation Risk",
      largest_risk_hint: "Position trading furthest above estimated fair value.",
      highest_weight: "Highest Weight",
      highest_weight_hint: "Position with the largest portfolio share.",
      biggest_winner: "Biggest Winner",
      biggest_winner_hint: "Strongest unrealized return.",
      biggest_loser: "Biggest Loser",
      biggest_loser_hint: "Largest unrealized loss.",
      concentration: "Concentration",
      concentration_high: "One position carries a significant portfolio share.",
      concentration_normal: "Weights look broadly balanced.",
    },
    table: {
      ticker: "Ticker",
      company: "Company",
      shares: "Shares",
      buy_price: "Buy Price",
      current_price: "Current",
      market_value: "Market Value",
      pnl: "P/L",
      weight: "Weight",
      fair_value: "Fair Value",
      upside: "Upside",
      ai_score: "AI Score",
      risk: "Risk",
      actions: "Actions",
      edit: "Edit",
      delete: "Delete",
    },
    dialog: {
      title_add: "Add Holding",
      title_edit: "Edit Holding",
      title_add_purchase: "Add to Existing Position",
      description: "Add a position to track its value against Stockaily's fair value.",
      description_add_purchase: "Record an additional purchase for {ticker}. Your average buy price will update automatically.",
      ticker: "Ticker",
      ticker_placeholder: "Search ticker or company (AAPL, Microsoft, Nvidia…)",
      hint_select_stock: "Select a stock from the list to continue.",
      err_select_stock: "Please select a stock from the suggestions.",
      shares: "Shares",
      shares_new: "New shares to add",
      buy_price: "Buy Price",
      buy_price_new: "Purchase price per share",
      buy_date: "Buy Date",
      buy_date_tooltip: "Optional for an initial holding; required for an additional purchase so returns can account for the cash flow. Entering a date does not yet reconstruct historical prices.",
      required: "required",
      purchase_date_required_hint: "Enter the actual purchase date so your return can account for this purchase. The date is required for additional purchases.",
      err_buy_date_required: "Enter the actual purchase date for this additional purchase.",
      err_buy_date: "Enter a valid purchase date no later than today (UTC).",
      buy_date_tooltip_label: "Why provide the Buy Date?",
      notes: "Notes",
      fees: "Fees",
      optional: "optional",
      add: "Add Holding",
      add_purchase: "Add Purchase",
      save: "Save Changes",
      saving: "Saving…",
      cancel: "Cancel",
      err_ticker: "Please enter a ticker symbol.",
      err_shares: "Shares must be greater than zero.",
      err_buy_price: "Buy price must be greater than zero.",
      existing_position: "Existing position",
      existing_shares: "Current shares",
      existing_avg_buy: "Current average buy price",
      projected_section: "After this purchase",
      projected_shares: "Total shares",
      projected_avg_buy: "New average buy price",
    },
    delete: {
      title: "Delete this position?",
      body: "{ticker} will be removed from your portfolio. Delete this position only if it was added by mistake. To record an actual sale, use Record Sale.",
      body_with_history: "{ticker} has {count} transaction(s) on record. Deleting will not create a realized P/L row. Delete this position only if it was added by mistake. To record an actual sale, use Record Sale.",
      confirm: "Delete",
      cancel: "Cancel",
    },
    locked: {
      title: "Portfolio Intelligence is a Premium feature",
      body: "Unlock portfolio tracking, fair value comparisons, and AI opportunity scoring across your positions.",
      cta: "Upgrade to Premium",
      footnote: "Includes Portfolio AI Review, fair value tracking, and growth dashboard.",
      demo: {
        badge: "Portfolio preview",
        badge_hint: "Example data. Not linked to a real account.",
        kpi_largest_risk: "Largest valuation risk",
        kpi_largest_risk_value: "1 holding above Fair Value",
        kpi_1y_growth: "1Y Portfolio Growth",
        kpi_risk_medium: "Medium",
        gated_hint: "Premium insight",
        chart_kicker: "Example portfolio view",
        chart_title: "Portfolio Growth",
        chart_a11y: "Illustrative Portfolio Growth chart",
        chart_disclosure: "Illustrative portfolio data for product preview purposes.",
        legend_portfolio: "Portfolio",
        legend_fair_value: "Estimated Fair Value",
        legend_sp500: "S&P 500",
        holdings_kicker: "Open positions",
        holdings_title: "Your holdings",
        holdings_count: "7 example positions",
        holdings_footer: "Example holdings \u00B7 Actual holdings and analytics available on Premium",
        tbl_ticker: "Ticker", tbl_shares: "Shares", tbl_buy: "Buy", tbl_current: "Current", tbl_mv: "Market value", tbl_pl: "P/L", tbl_weight: "Weight", tbl_fair: "Fair", tbl_upside: "Upside", tbl_score: "Score", tbl_risk: "Risk",
        pl_kicker: "Profit & loss", pl_title: "P/L breakdown", pl_unrealized: "Unrealized P/L", pl_realized: "Realized P/L", pl_total: "Total P/L",
        closed_kicker: "Historical", closed_title: "Closed positions", closed_hint: "Track realized returns per exit",
        closed_empty_title: "No closed positions yet in this example.",
        closed_empty_sub: "Realized P/L, holding period and post-sale performance appear here after you record a sale.",
        ai_kicker: "AI intelligence", ai_title: "AI Portfolio Review", ai_premium_pill: "Premium",
        ai_pill_intel: "Portfolio Intelligence \u00B7 AI Assisted",
        ai_pill_cached: "Cached",
        ai_warn_title: "Portfolio changed since this analysis",
        ai_warn_body: "Update the analysis to reflect the latest holdings.",
        ai_medium: "Medium",
        ai_confidence_label: "Confidence:",
        ai_intro: "Your portfolio shows strong business quality, while valuation risk remains concentrated in two positions. Consider reviewing concentration and the lowest-confidence holding before earnings.",
        ai_strengths: "Strengths", ai_weaknesses: "Weaknesses", ai_concentration: "Concentration Risks", ai_research: "Research Priorities",
        ai_footer: "Example review structure \u00B7 Full personalized review is Premium",
        i_top_opp_label: "Top opportunity", i_top_opp_title: "Example holding \u00B7 +18% upside", i_top_opp_sub: "Above fair value expectation",
        i_val_risk_label: "Largest valuation risk", i_val_risk_title: "1 holding trades 16% above Fair Value", i_val_risk_sub: "Consider trimming or hedging",
        i_weight_label: "Highest weight", i_weight_title: "Example holding \u00B7 27% of portfolio", i_weight_sub: "Concentration flag \u2014 review",
        i_winner_label: "Biggest winner", i_winner_title: "Example holding \u00B7 +44% since buy", i_winner_sub: "Momentum + earnings tailwind",
        i_loser_label: "Biggest loser", i_loser_title: "Example holding \u00B7 -10% since buy", i_loser_sub: "Re-evaluate thesis \u2014 review",
        i_sector_label: "Sector concentration", i_sector_title: "Technology \u00B7 42%", i_sector_sub: "Example sector mix",
        // Legacy keys still used by other flows
        mod_sector_label: "Sector concentration", mod_sector_title: "Technology \u00B7 42%", mod_sector_sub: "Example sector mix",
        mod_risk_label: "Largest valuation risk", mod_risk_title: "1 holding trades 16% above estimated Fair Value", mod_risk_sub: "",
        mod_opp_label: "Strongest opportunity", mod_opp_title: "Example holding \u00B7 18% estimated upside", mod_opp_sub: "",
        mod_ai_label: "Portfolio AI Review",
        mod_ai_body: "Your portfolio shows strong business quality, while valuation risk remains concentrated in two positions. Consider reviewing concentration and the lowest-confidence holding.",
        mod_ai_footer: "Example review \u00B7 Full review is Premium",
      },
    },
    loggedout: {
      title: "Understand your entire portfolio with Stockaily.",
      body: "Track performance, Fair Value, opportunity and risk across every holding.",
      signup_cta: "Create free account",
      login_cta: "Log in",
      footnote: "Free account. No card required.",
      view_premium_link: "View Premium features",
    },
    upsell: {
      title: "Unlock Portfolio Intelligence",
      body: "See valuation, performance and risk across your entire portfolio.",
      cta: "Explore Premium",
      footnote: "You'll review features and pricing before checkout. No charge occurs at this step.",
    },
    empty: {
      title: "Build your portfolio intelligence layer",
      body: "Add your first position to track performance, fair value, opportunity score, and risk in one place.",
      cta: "Add Holding",
      learn_more: "Learn how scoring works",
      feature_fair_value: "Track fair value across holdings",
      feature_upside: "See portfolio-level upside and downside",
      feature_risk: "Review risk and opportunity by position",
    },
    // Phase 8.C-3 — special state shown when the user has sold ALL open
    // positions but still has a closed-position history we don't want
    // to hide.
    no_open_with_history: {
      heading: "No open positions",
      subtext: "Add a new holding to start a new portfolio lifecycle.",
    },
    // Phase 8.C-3 follow-up — Legacy opening-lot backfill (Owner/Admin
    // tooling only; never shown to regular users).
    backfill_legacy: {
      heading: "Backfill Legacy Opening Transactions",
      description: "One-time, idempotent backfill that creates exactly one synthetic opening BUY transaction for each eligible legacy open holding that has no transaction history. Aggregates (shares, average buy price, cost basis) are never modified.",
      run_dry_run: "Run dry-run",
      apply: "Apply backfill",
      reset: "Reset",
      eligible: "Eligible lifecycles",
      skipped: "Skipped lifecycles",
      no_eligible: "No eligible lifecycles — nothing to apply.",
      eligible_count: "Eligible: {count}",
      skipped_count: "Skipped: {count}",
      confirm_label: "I understand that synthetic opening BUY transactions will be created for eligible legacy holdings.",
      column_ticker: "Ticker",
      column_shares: "Shares",
      column_avg_price: "Avg buy price",
      column_cost_basis: "Synthetic cost basis",
      column_reconcile: "Reconcile diff",
      column_purchase_date: "Purchase date",
      column_date_status: "Date status",
      column_reason: "Skip reason",
      created_heading: "Created transactions",
      created_count: "Created {count} synthetic BUY transaction(s)",
      affected_tickers: "Affected tickers",
      dry_run_first: "Run a dry-run first.",
      production_warning: "Confirm the resolved environment + user_id before applying.",
    },
    // Phase 8.D — Daily Portfolio Snapshots admin panel.
    daily_snapshots: {
      heading: "Daily Portfolio Snapshots",
      description: "Background snapshot job for Premium portfolios. One canonical row per user per UTC day. Zero LLM calls.",
      scheduler: "Scheduler",
      enabled: "Enabled",
      disabled: "Disabled",
      registered: "Registered",
      not_registered: "Not registered",
      cron: "Cron",
      env: "Environment",
      lock: "Job lock",
      lock_free: "Free",
      lock_held: "Held by {holder} since {since}",
      eligible: "Eligible Premium users",
      last_run: "Last job run",
      run_dry_run: "Run dry-run",
      run_now: "Run snapshot job now",
      retry_failed: "Retry failed users",
      view_last: "View last job",
      confirm_run_now: "I understand a daily snapshot job will be executed and snapshots will be written.",
      confirm_retry: "I understand failed users from the selected prior job_run_id will be retried.",
      col_user: "User",
      col_status: "Status",
      col_reason: "Reason",
      col_open_holdings: "Open holdings",
      col_missing: "Missing tickers",
      // Phase 8.D visibility fix — per-user results table columns.
      col_persisted: "Persisted",
      col_coverage: "Coverage",
      col_partial_reason: "Partial reason",
      col_stale: "Stale tickers",
      persisted_yes: "Yes",
      persisted_no: "No",
      dim_price: "Price",
      dim_fair_value: "Fair value",
      dim_score: "AI score",
      dim_risk: "Risk",
      no_eligible: "No eligible users found.",
      job_status_complete: "complete",
      job_status_partial: "partial",
      job_status_failed: "failed",
      // Phase 8.D — additional admin UI labels (Owner Console).
      refresh_status: "Refresh status",
      production_warning: "This will write or update today\u2019s canonical portfolio snapshots for all eligible Premium users in the selected scope. The operation is idempotent per user and UTC date, but it writes real snapshot data.",
      force_refresh_prices: "Force refresh prices",
      force_refresh_prices_hint: "Temporarily unavailable; this option previously bypassed snapshot write protection.",
      batch_limit_label: "Batch limit (optional)",
      batch_limit_placeholder: "e.g. 50",
      batch_limit_invalid: "Batch limit must be a positive integer.",
      user_scope_label: "Target user (optional)",
      user_scope_all: "All eligible Premium users",
      user_scope_selected: "Limited to user_id: {user_id}",
      retry_target_label: "Retry job_run_id",
      retry_target_placeholder: "Paste a prior job_run_id to retry",
      retry_target_autofilled: "Auto-filled from last {status} run",
      retry_no_failed: "No failed or partial run available to retry.",
      retry_target_required: "A job_run_id is required to retry.",
      snapshot_date_label: "Current UTC snapshot date",
      eligible_count_estimate: "Estimated eligible users",
      preview_users_heading: "Preview (first {count} users)",
      results_heading: "Per-user results",
      no_last_run: "No prior job run recorded yet.",
      dedup_ratio: "Dedup ratio",
      api_calls: "API calls",
      cache_hits: "Cache hits",
      duration: "Duration",
      seconds: "s",
      reset: "Clear report",
      job_run_id_label: "job_run_id",
      started_at: "Started at",
      finished_at: "Finished at",
      status_label: "Status",
      success_count: "Successful",
      partial_count: "Partial",
      failed_count: "Failed",
      unique_tickers_label: "Unique tickers",
      lock_status_free: "Lock free \u2014 safe to run.",
      lock_status_held: "Job lock currently held by {holder} (since {since}). Wait or release before running.",
      scheduler_disabled_banner: "Scheduler is currently disabled (PORTFOLIO_DAILY_SNAPSHOTS_ENABLED=false). Manual runs are still available.",
    },
    action_menu: {
      label: "Actions",
      add_purchase: "Add Purchase",
      record_sale: "Record Sale",
      view_transactions: "View Transactions",
      edit_metadata: "Edit Position Metadata",
      delete_incorrect: "Delete Incorrect Position",
    },
    sell: {
      title: "Record Sale",
      open_status: "Open lifecycle",
      available_shares: "Available shares",
      avg_buy_price: "Average buy price",
      open_cost_basis: "Open cost basis",
      current_price: "Current price",
      current_unrealized: "Current unrealized P/L",
      field_shares: "Shares to sell",
      field_price: "Sale price per share",
      field_date: "Sale Date",
      field_date_tooltip: "Recommended for accurate holding periods and transaction history.",
      field_date_tooltip_label: "Why provide the Sale Date?",
      field_fees: "Fees",
      field_notes: "Notes (optional)",
      sell_all: "Sell all",
      preview_title: "Sale preview",
      preview_caveat: "Informational only — backend calculation is authoritative.",
      preview_gross: "Gross proceeds",
      preview_fees: "Fees",
      preview_net: "Net proceeds",
      preview_cost_basis_released: "Cost basis released",
      preview_realized_pl: "Estimated realized P/L",
      preview_realized_pl_pct: "Estimated realized P/L %",
      preview_remaining_shares: "Remaining shares",
      preview_remaining_cost_basis: "Remaining cost basis",
      preview_status: "Position status after sale",
      status_open: "Open",
      status_closed: "Closed after sale",
      submit: "Record Sale",
      cancel: "Cancel",
      submitting: "Recording sale\u2026",
      validation_shares_positive: "Shares must be greater than 0.",
      validation_shares_max: "Shares cannot exceed available shares.",
      validation_price_positive: "Sale price must be greater than 0.",
      validation_fees_nonneg: "Fees cannot be negative.",
      validation_date_invalid: "Sale date is invalid.",
      error_conflict: "The available share quantity changed. Review the updated position and try again.",
      error_insufficient: "Not enough shares available to record this sale.",
      error_generic: "We couldn't record the sale. Please try again.",
    },
    closed: {
      heading: "Closed Positions",
      empty: "Closed positions will appear here after you record a complete sale.",
      collapse_show: "Show",
      collapse_hide: "Hide",
      col_ticker: "Ticker",
      col_company: "Company",
      col_first_buy: "First Buy Date",
      col_final_sale: "Final Sale Date",
      col_total_shares: "Total Shares Sold",
      col_avg_buy: "Avg Buy Price",
      col_avg_sell: "Avg Sell Price",
      col_proceeds: "Total Proceeds",
      col_cost_basis: "Total Cost Basis",
      col_fees: "Total Fees",
      col_realized_pl: "Realized P/L",
      col_realized_pct: "Realized P/L %",
      col_holding_period: "Holding Period",
      col_transactions: "Transactions",
      col_action: "Action",
      view_history: "View Transactions",
      holding_period_days: "{days} day(s)",
      holding_period_unavailable: "Unavailable",
      holding_period_unavailable_tooltip: "The original purchase date is incomplete, so an exact holding period cannot be calculated.",
      holding_period_unavailable_tooltip_label: "Why is the holding period unavailable?",
    },
    history: {
      title: "{ticker} Transaction History",
      lifecycle_status_open: "Open lifecycle",
      lifecycle_status_closed: "Closed lifecycle",
      first_txn: "First transaction",
      latest_txn: "Latest transaction",
      col_date: "Date",
      col_type: "Type",
      col_shares: "Shares",
      col_price: "Price / share",
      col_fees: "Fees",
      col_gross: "Gross",
      col_net: "Net",
      col_realized_pl: "Realized P/L",
      col_notes: "Notes",
      col_source: "Source",
      type_buy: "BUY",
      type_sell: "SELL",
      empty: "No transactions on record.",
      error: "We couldn\u2019t load the transaction history.",
      retry: "Try again",
      close: "Close",
      date_unavailable: "Date unavailable",
      add_purchase_date: "Add purchase date",
      confirm_purchase_date: "Confirm date",
      date_confirmation_hint: "Enter the actual purchase date. Only this missing date will change; shares, price and fees stay the same. If the date is unknown, leave it blank.",
      date_save_error: "We couldn’t save the date. Reload the transaction history and try again.",
      date_conflict: "This transaction already has a date. Reload the transaction history to see the current value.",
      source_label: "Source",
      source_labels: {
        migrated_legacy_opening_lot: "Initial portfolio entry",
        user_opening_lot: "Initial portfolio entry",
        user_additional_purchase: "Additional purchase",
        user_sell: "Recorded sale",
        imported: "Imported transaction",
        unknown: "Recorded transaction",
      },
    },
    pl_card: {
      title: "Profit / Loss breakdown",
      unrealized: "Unrealized P/L",
      realized: "Realized P/L",
      total: "Total P/L",
      tooltip_unrealized: "Profit or loss on currently open positions, based on the latest available prices.",
      tooltip_realized: "Profit or loss from recorded SELL transactions. Historical and informational \u2014 sale proceeds are not held inside Stockaily.",
      tooltip_total: "Realized P/L plus Unrealized P/L. Portfolio Value above includes only open positions.",
    },
    stale_after_sell: "Portfolio changed since this analysis",
    delete_warning_transactions: "This position has {count} transaction(s). Deletion does not create realized P/L.",
    delete_warning_use_sale: "If this represents a real sale, cancel and use Record Sale instead.",
    toast: {
      holding_added: "Holding added.",
      holding_merged: "Added to existing {ticker} position.",
      holding_updated: "Holding updated.",
      holding_deleted: "Holding removed.",
      prices_refreshed: "Prices refreshed.",
      analysis_refreshed: "Portfolio analysis refreshed.",
      sale_partial: "Sale recorded. The position remains open.",
      sale_full: "Sale recorded. The position has been closed.",
    },
    error: {
      load_failed: "We couldn't load your portfolio. Please retry.",
      duplicate_holding: "This holding already exists. Edit the existing position instead.",
      holdings_limit: "You've reached the Phase 1 limit of 50 holdings.",
      premium_required: "Portfolio tracking is a Premium feature.",
      save_failed: "Couldn't save the holding. Please try again.",
      delete_failed: "Couldn't remove the holding. Please try again.",
      refresh_prices_failed: "Couldn't refresh prices right now.",
      refresh_analysis_failed: "Couldn't refresh portfolio analysis.",
      cooldown_prices: "Price refresh is on cooldown. Try again shortly.",
      cooldown_analysis: "Analysis refresh is on cooldown. Try again later.",
      daily_limit: "Daily portfolio AI refresh limit reached. Your portfolio keeps the latest analysis.",
      hard_limit: "Daily analysis cap reached. Refreshes will resume tomorrow.",
      job_running: "An analysis refresh is already running.",
    },
  },

  // ----------------------------------------------------------------
  // Consent Foundation & Cookie Banner (2026-08-02)
  // Bilingual copy for the first-visit banner + granular settings
  // dialog + permanent footer control.  Kept factual — Analytics is
  // described as the intended service for the next workstream; no
  // claim that PostHog is currently active or collecting data.
  // ----------------------------------------------------------------
  consent: {
    banner: {
      title: "Your privacy choices",
      body:
        "Stockaily uses only necessary storage to keep the site secure, remember your language, and remember your consent choice. Optional analytics (PostHog) is not active and will not run unless you actively consent. You can change your choice at any time from Cookie settings in the footer.",
      link_cookies: "Cookie Policy",
      link_privacy: "Privacy Policy",
      accept_all: "Accept all",
      necessary_only: "Necessary only",
      settings: "Settings",
      close_label: "Close cookie banner",
    },
    dialog: {
      title: "Cookie settings",
      subtitle:
        "You control which optional categories Stockaily may enable. Necessary storage is always active because the site cannot work without it.",
      category_necessary_title: "Necessary",
      category_necessary_body:
        "Consent storage, authentication session, language preference, and other storage strictly required to deliver the website and its core account features. Always active — this category cannot be turned off.",
      category_necessary_locked: "Always active",
      category_analytics_title: "Analytics",
      category_analytics_body:
        "Product analytics via PostHog (EU Cloud, Paris/Frankfurt region). Only loads and sends events after you consent here. IP addresses are discarded, autocapture and session replay are disabled, and only a fixed list of anonymous product-usage events is sent — no names, emails, tickers, portfolio contents or financial values.",
      save: "Save selection",
      accept_all: "Accept all",
      necessary_only: "Necessary only",
      cancel: "Cancel",
      current_state: "Your saved choice",
      state_none: "No decision recorded yet",
      state_all: "Optional analytics: on",
      state_necessary_only: "Optional analytics: off",
      updated_at: "Last updated",
    },
    footer_settings_button: "Cookie settings",
  },
  research_transparency: {
    // WS-1 compact / long product-disclaimer copy — the same regulated
    // English text as `ws1RegCopy.WS1_DISCLAIMER_TEXT`, exposed via
    // i18n so the Disclaimer component can be forced into a specific
    // language on route-scoped pages (e.g. /de/research-transparenz)
    // without mutating the persisted app-wide locale. EN is verbatim
    // from the canonical constant.
    disclaimer_compact:
      "Model-based research, not personalised advice. Investing involves risk.",
    disclaimer_long:
      "Stockaily provides model-based financial research and information. It is not personalised investment advice and does not consider your individual circumstances. Estimates may be wrong. Investing involves risk and you make your own decisions.",
    methodology_long_v1:
      "Weighted Fair Value model combining DCF, sector P/E and fundamental factors (Methodology v1)",
    methodology_short_v1: "Blended DCF, sector P/E & fundamentals (v1)",
    methodology_missing: "Methodology metadata unavailable",
    conflicts_no_compensation:
      "Stockaily receives no compensation from the analyzed company.",
    corrections_none: "No corrections published.",
    corrections_as_of_prefix: "as of",
    methodology_label: "Methodology",
    conflicts_label: "Conflicts of interest",
    corrections_label: "Corrections",
    technical_details: "Technical details",
    tech_schema: "Schema",
    tech_regcopy: "Regulatory copy",
    tech_content: "Content nature",
    tech_provider: "Provider",
    tech_model: "Model identifier",
    // Correction reason / status codes — Slice C localization.
    // Keyed on the canonical backend `CorrectionStatus` enum value
    // returned inside `correction_notice.status`. The frontend prefers
    // the localized label; the backend English `label` field is used
    // only as a last-resort fallback for unknown/future statuses so
    // the notice is never silently hidden.
    correction_status_metadata_corrected: "Metadata updated",
    correction_status_data_corrected: "Underlying data corrected",
    correction_status_methodology_updated: "Methodology updated",
    correction_status_material_research_updated: "Research updated",
    correction_status_superseded: "Superseded by a newer analysis",
    correction_status_withdrawn: "Withdrawn",
    correction_status_unknown: "Correction published",
    // Slice C — Central Research Transparency page.
    page_title: "Research Transparency",
    page_intro:
      "How Stockaily calculates Fair Value and the AI Opportunity Score, what our AI does and does not do, and which data we use.",
    ai_assisted_explanation: "AI-assisted explanation based on Stockaily model outputs",
    // Section headings
    section_what_we_provide: "What Stockaily provides",
    section_fair_value: "How Fair Value is calculated",
    section_ai_score: "How the AI Opportunity Score is calculated",
    section_role_of_ai: "What AI does — and does not do",
    section_data_sources: "Data sources, timestamps and data quality",
    section_assumptions: "Assumptions, uncertainty and missing data",
    section_conflicts: "Conflicts of interest",
    section_corrections: "Corrections",
    section_limitations: "Important limitations",
    section_tech_details: "Technical details",
    // What we provide
    what_body:
      "Stockaily is research, analysis and information software. It produces model-based estimates of Fair Value, a numerical AI Opportunity Score and supporting commentary. It does not provide personalized investment advice, individualized Buy/Sell/Hold instructions or position-sizing recommendations, and it does not guarantee accuracy, performance, returns or outcomes.",
    // Fair Value section
    fv_intro:
      "The displayed Fair Value is a weighted blend of a simplified discounted cash-flow (DCF) model, a sector-relative P/E anchor and quality-adjusted fundamental factors. It is calculated deterministically before any AI narrative is produced.",
    fv_weights_title: "Baseline component weights",
    fv_weight_dcf: "Simplified DCF",
    fv_weight_pe: "Sector-relative P/E anchor",
    fv_weight_fcf: "FCF-quality factor",
    fv_weight_profitability: "Profitability factor",
    fv_weight_rev_growth: "Revenue-growth factor",
    fv_weight_eps_growth: "Earnings-growth factor",
    fv_weight_bs: "Balance-sheet factor",
    fv_weights_note:
      "These are baseline weights, not fixed weights for every analysis.",
    fv_fallbacks_title: "How partial data is handled",
    fv_fallback_dcf:
      "When DCF cannot be calculated because required cash-flow inputs are invalid or unavailable, its baseline weight is reassigned to the relative-valuation component.",
    fv_fallback_missing:
      "Other unavailable components are omitted and the remaining weights are normalized.",
    fv_fallback_extreme:
      "If no valuation component is available, an extreme fallback uses the current market price so the interface degrades honestly rather than silently.",
    fv_range_title: "Fair Value range",
    fv_range_body:
      "The Fair Value range is derived from the central Fair Value using a mode-dependent uncertainty band. Lower data completeness can widen that band. The range is not a probability interval and is not a fixed price target.",
    fv_sector_ref_title: "Sector reference values",
    fv_sector_ref_body:
      "Sector P/E reference values and DCF terminal multiples are internal Stockaily reference values. They are not live-calculated sector medians.",
    // AI Opportunity Score section
    ais_intro:
      "The numerical AI Opportunity Score is calculated deterministically and rule-based. Generative AI / the LLM does not calculate, choose or override the numerical score. The name reflects our product terminology, not the calculation method.",
    ais_weights_title: "Baseline formula",
    ais_weight_valuation: "Fair Value / upside",
    ais_weight_quality: "Business quality",
    ais_weight_growth: "Growth",
    ais_weight_safety: "Financial safety",
    ais_completeness_note:
      "Lower data completeness reduces the resulting score via a confidence multiplier.",
    ais_guardrails_note:
      "Deterministic guardrails and the institutional risk / value-trap overlay can cap the score or change the displayed signal or status. This overlay is applied after Fair Value has been calculated and does not change the displayed Fair Value.",
    ais_score_vs_classification:
      "Score and classification are separate layers. The AI Opportunity Score combines valuation, business quality, growth and financial stability into a numerical result. The final status, such as Opportunity, Neutral or Risk, is not determined by the score alone. Stockaily also considers the discount or premium to estimated Fair Value, upside or downside, risk level, data quality and deterministic guardrails. A high score alone therefore does not guarantee an Opportunity or Top Opportunity classification.",
    // Role of AI section
    ai_deterministic_title: "Deterministic / model-based",
    ai_deterministic_body:
      "Fair Value, Fair Value range, upside / downside, the numerical AI Opportunity Score, KPIs, risk flags, signal and status are deterministic model outputs. They are computed before any AI narrative is generated and remain available if AI generation is unavailable.",
    ai_llm_title: "AI-generated narrative",
    ai_llm_body:
      "AI assists with summaries, explanations, bull / bear cases and other narrative content. AI narrative is constrained to the model facts supplied to it. Consistency controls suppress narrative that would contradict the deterministic Fair Value. When AI generation is unavailable, a deterministic or templated fallback is served.",
    ai_does_not_title: "What AI does not do",
    ai_does_not_body:
      "AI does not independently research companies, does not calculate Fair Value, does not calculate, select or override the numerical AI Opportunity Score, does not calculate deterministic KPIs and does not make investment decisions.",
    // Data sources section
    ds_intro:
      "Every analysis carries structured metadata so you can see what data was used and how fresh it is.",
    ds_field_data_as_of: "Data as-of",
    ds_field_data_as_of_desc:
      "Reference timestamp for the underlying fundamental data used in the analysis.",
    ds_field_market_price: "Market price as-of",
    ds_field_market_price_desc:
      "Timestamp of the market price the analysis was anchored on. Market data is not real-time.",
    ds_field_valuation: "Valuation as-of",
    ds_field_valuation_desc:
      "Timestamp at which the Fair Value blend was computed.",
    ds_field_generated: "Analysis generated at",
    ds_field_generated_desc:
      "Timestamp at which this analysis payload was produced.",
    ds_field_quality: "Data quality status",
    ds_field_quality_desc:
      "Reports whether the analysis used current, stale or partial data.",
    ds_field_stale: "Stale-data indicator",
    ds_field_stale_desc:
      "Names which specific input (market price, quarterly or annual fundamentals, cached research) is stale, if any.",
    ds_field_estimated: "Estimated fields",
    ds_field_estimated_desc:
      "List of fields whose value had to be estimated because the raw input was missing.",
    ds_field_unavailable: "Unavailable fields",
    ds_field_unavailable_desc:
      "List of fields explicitly marked as unavailable — never substituted with a fabricated value.",
    ds_field_provider: "Provider category",
    ds_field_provider_desc:
      "Whether the analysis is deterministic-only or was enriched with an external AI narrative provider.",
    ds_field_sources: "Source categories",
    ds_field_sources_desc:
      "Which upstream categories (market data, fundamentals, filings, deterministic model, AI narrative provider) contributed to the analysis.",
    ds_no_last_reviewed_note:
      "There is currently no separate methodology-update date.",
    // Assumptions section
    assump_intro:
      "Certain assumptions are fixed in the model. Others are recomputed per analysis based on the available data.",
    assump_fixed_title: "Fixed in code",
    assump_fixed_dcf_horizon: "Five-year explicit DCF projection window",
    assump_fixed_growth_cap: "Growth rate capped at 25 %",
    assump_fixed_discount: "Discount rate 8–13 %, modulated by mode and beta",
    assump_fixed_terminal:
      "Terminal multiples drawn from an internal sector reference table",
    assump_fixed_band:
      "Mode-dependent uncertainty band (12 % Standard, 20 % Growth, 30 % Speculative) plus up to 10 pp data-completeness penalty",
    assump_fixed_caps:
      "Score guardrails (caps at 59, 64 and 55) applied to extreme cases",
    assump_dynamic_title: "Recomputed per analysis",
    assump_dynamic_body:
      "Mode, classification, sub-scores, blended Fair Value, range, upside, AI Opportunity Score, signal, risk level and time horizon are recalculated for every analysis from the current inputs.",
    assump_missing_title: "Missing-data behavior",
    assump_missing_body:
      "Missing inputs are excluded from their sub-scores and their weights are renormalized. Values are never silently coerced to zero. If key inputs are absent the analysis is marked with a lower data-quality status.",
    // Conflicts section (already exists)
    conflicts_business_fact_title: "Current business fact",
    conflicts_business_fact_body:
      "Stockaily currently receives no direct or indirect compensation from any analyzed company or issuer in exchange for making a company or its security available for analysis, analyzing or featuring it, ranking it, portraying it favorably or influencing its Fair Value, AI Opportunity Score or any other analytical output. There is currently no issuer-paid coverage, sponsored analysis or comparable compensation model.",
    conflicts_maintenance_note:
      "This disclosure and any related transparency and legal copy must be reviewed before any future issuer-compensation, sponsored-coverage or comparable commercial-partnership model is introduced.",
    // Corrections section
    corrections_intro:
      "When an analysis is materially corrected, the affected analysis carries a canonical correction status which is rendered here in the current language.",
    corrections_current_state_title: "Current state",
    corrections_current_state_body:
      "Correction metadata and display support exist. There is currently no runtime editorial workflow that transitions analyses into a published correction state. Implementing that workflow will require additional backend and editorial work.",
    // Limitations
    limitations_body:
      "Model-based estimates can be incomplete, uncertain or incorrect. Nothing on this page or in a stock analysis constitutes personalized investment advice, an individualized Buy / Sell / Hold instruction, a position-sizing recommendation or a guarantee of accuracy, performance, returns or outcomes.",
    limitations_link_disclaimer: "Read the full Financial Disclaimer",
  },
  composer: {
    published_title: "Published snapshot",
    currently_public: "Currently public",
    no_active_fallback_badge: "Static fallback",
    no_active_body:
      "No active homepage snapshot. The public homepage is serving the pre-launch static fallback.",
    published_at: "Published",
    reload: "Reload",
    reload_aria: "Reload published snapshot and draft",
    republish: "Republish last successful",
    draft_title: "Draft composer",
    draft_not_public_hint:
      "Draft changes are not public until published.",
    updated_at: "Updated",
    readiness_counter: "{ready}/4 ready",
    state_empty: "Empty",
    state_partial: "In progress",
    state_ready: "Ready to publish",
    state_published: "Published",
    slot_label_top_opportunity: "Top Opportunity",
    slot_label_opportunity: "Opportunity",
    slot_label_fairly_valued: "Fairly Valued / Neutral",
    slot_label_risk: "Risk",
    slot_state_empty: "Empty — pin a ticker below",
    slot_state_valid: "Valid",
    slot_state_invalid: "Invalid",
    src_manual_pin: "Manually pinned",
    src_generated: "Automatically selected",
    src_inherited: "Inherited",
    chart_available: "Chart data available",
    chart_missing: "Chart data missing",
    field_status: "Status",
    field_score: "Score",
    field_price: "Price",
    field_fair_value: "Fair value",
    invalid_reason_stale_analysis: "Analysis stale",
    invalid_reason_classification_mismatch: "Classification no longer matches",
    invalid_reason_chart_series_missing_or_empty: "Chart data missing",
    invalid_reason_required_fields_missing: "Card is missing required fields",
    invalid_reason_duplicate_ticker_in_draft: "Duplicate ticker across slots",
    pin_ticker_label: "Ticker for {slot}",
    pin_action: "Pin",
    pin_action_aria: "Pin ticker to {slot}",
    generate_remaining: "Generate remaining slots",
    generate_hint:
      "Generate preserves valid manual pins and only fills eligible remaining slots. It never fabricates missing cards or charts.",
    publish_now: "Publish now",
    publish_disabled_incomplete:
      "Complete all four valid slots before publishing.",
    publish_disabled_reload:
      "Reload the current draft — the server does not consider it ready.",
    discard_draft: "Discard draft",
    discard_action_aria: "Discard the current unfinished draft",
    discard_confirm_title: "Discard unfinished draft?",
    discard_confirm_body:
      "This removes the unfinished draft only. The currently published homepage snapshot remains unchanged.",
    discard_confirm_action: "Discard draft",
    cancel: "Cancel",
    toast_generate_partial: "Draft updated. Not all slots are ready yet.",
    toast_generate_no_candidate: "No suitable Opportunity was found among the latest available analyses.",
    no_eligible_candidate: "No suitable Opportunity was found among the latest available analyses.",
    mismatch_warning: "Current analysis changed from {published} to {current}. The published card remains unchanged until you replace and publish it.",
    snapshot_provenance: "Example analysis · Snapshot published: {date}",
    toast_generate_published: "Draft was fully ready — published to homepage.",
    toast_publish_success: "Homepage snapshot published.",
    toast_already_published: "Snapshot already published — no changes made.",
    toast_discard_success: "Draft discarded. Published snapshot unchanged.",
    toast_republish_success:
      "Republished last successful snapshot. Draft preserved.",
    announce_pinned: "Pinned {ticker} to {slot}.",
    announce_generated: "Draft updated by Generate.",
    announce_published: "Homepage snapshot published.",
    announce_discarded: "Draft discarded.",
    err_load_failed: "Failed to load the homepage snapshot.",
    err_session_expired: "Session expired. Please sign in again.",
    err_recent_auth_required:
      "Re-authentication required. Please confirm your identity to continue.",
    err_stale_revision:
      "The draft changed elsewhere. It has been reloaded — please retry.",
    err_pin_400: "Cannot pin this ticker to this slot.",
    err_pin_generic: "Pin failed. Please try again.",
    err_publish_400: "Draft is not ready to publish.",
    err_publish_generic: "Publication failed. Please try again.",
    err_discard_generic: "Discard failed. Please try again.",
    err_generate: "Generate failed. Please try again.",
    err_republish_generic: "Republish failed. Please try again.",
    err_busy: "Please wait for the current operation to finish.",
  },
  broker_survey: {
    title: "Which broker(s) do you use?",
    helper: "Select all that apply.",
    explanation: "Your answer helps us improve onboarding and prioritize future integrations. It does not connect Stockaily to your broker.",
    search_placeholder: "Search brokers",
    other_label: "Other",
    other_placeholder: "Which broker?",
    no_broker_label: "I don't use a broker yet",
    continue: "Continue",
    submitting: "Saving…",
    logout: "Sign out",
    privacy_link: "Privacy policy",
    err_required: "Please select at least one option.",
    err_other_required: "Please tell us which broker you use.",
    err_generic: "Could not save your answer. Please try again.",
    err_no_broker_exclusive: "\"I don't use a broker yet\" cannot be combined with other selections.",
    multi_note: "Multi-select — pick every broker you actually use.",
  },
  early_access: {
    nav_back: "Back to Stockaily",
    seo_title: "Early Access — Stockaily",
    seo_description: "Join Stockaily Early Access. Company fundamentals, valuation and risk brought together in three clear, model-based signals for structured stock research. Free to join — no payment before launch.",
    og_title: "Stockaily Early Access",
    og_description: "Company fundamentals, valuation and risk brought together in three clear, model-based signals for structured stock research. Free to join — no payment before launch.",
    hero_eyebrow: "Early Access",
    hero_title: "Invest with more conviction.",
    hero_subtitle: "Stockaily brings company fundamentals, valuation and risk together in three clear, model-based signals for structured stock research.",
    benefit_opp_title: "Opportunity Score",
    benefit_opp_body: "A single, explainable score that summarises how attractive a stock looks on the fundamentals that matter.",
    benefit_fv_title: "Fair Value",
    benefit_fv_body: "A transparent, model-based estimate of what the business is worth today \u2014 shown as context, never as a recommendation.",
    benefit_risk_title: "Risk",
    benefit_risk_body: "A plain-language view of the drawdown, concentration and quality risks that are structurally relevant for a position.",
    preview_label: "Product preview",
    preview_opp: "Opportunity Score",
    preview_fv: "Fair Value",
    preview_risk: "Risk",
    preview_opp_value: "Model-based opportunity assessment",
    preview_fv_value: "Transparent valuation estimate",
    preview_risk_value: "Structured risk assessment",
    preview_note: "Illustrative preview \u2014 Stockaily shows model-based signals, not forecasts, promises or personalised advice.",
    form_eyebrow: "Reserve your spot",
    form_title: "Join the Early Access list.",
    form_subtitle: "Free to join. No payment is taken before launch.",
    form_email_label: "Email address",
    form_email_placeholder: "you@example.com",
    form_name_label: "First name (optional)",
    form_name_placeholder: "Alex",
    form_consent_label: "I would like to receive Early Access and product emails from Stockaily. I can withdraw my consent at any time.",
    form_consent_policy_prefix: "See our",
    form_consent_policy_link: "Privacy Policy",
    form_submit: "Join Early Access",
    form_submitting: "Submitting\u2026",
    form_err_email_required: "Please enter your email address.",
    form_err_email_invalid: "That email address doesn't look right.",
    form_err_consent_required: "Please tick the consent box to continue.",
    form_err_generic: "Something went wrong. Please try again.",
    form_success_title: "Check your inbox",
    form_success_body: "If this address is eligible, we just sent a confirmation link. Open it to complete your Early Access signup. The confirmation link expires in 7 days.",
    offer_text: "The first 100 confirmed Early Access members will receive a 20% Founding Member discount at launch.",
    offer_fine_print: "Joining is free. No payment is taken before launch. The discount code will be sent by email at launch.",
    disclaimer: "Stockaily provides model-based research and information \u2014 not personal investment advice.",
    legal_privacy: "Privacy Policy",
    legal_imprint: "Imprint",
    legal_disclaimer: "Disclaimer",
    legal_methodology: "Methodology",
    confirmation_already_title: "Already confirmed",
    confirmation_title: "You're confirmed",
    confirmation_already: "You're already confirmed. We will contact you by email when Stockaily launches.",
    confirmation_success: "You're confirmed. We will contact you by email when Stockaily launches.",
  },
};
export default en;

